import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
    Search,
    Funnel,
    FunnelPlus,
    Edit2,
    Plus,
    MoreHorizontal,
    ChevronsUpDown,
    ChevronLeft,
    ChevronRight,
    X,
    Copy,
    SquareArrowOutUpRight,
    CircleCheck,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { useHistory, useLocation } from "react-router-dom";
import apiHelper from '../../apiHelper';
import noBrandImage from '../../assets/img/brand/no-brand-img.png';
import emptySearchImg from '../../assets/img/brand/empty-search.png';
import ConfirmArchiveModal from '../../components/ConfirmArchiveModal';
import ConfirmDeactivateModal from '../../components/ConfirmDeactivateModal';
import DiscardConfirmationModal from '../../components/DiscardConfirmationModal';
import { useLoader } from '../../components/GlobalLoader';
import AlertNotification from '../../components/AlertNotification';
import DynamicTable from '../../components/DynamicTable'; // Import DynamicTable component
import Pagination from '../../components/Pagination'; // Import Pagination component

const PAGE_SIZE = 10;

// Updated Sort icon component with color states
const SortIcon = ({ sortConfig, columnKey }) => {
    const isActive = sortConfig.key === columnKey;
    const isAscending = isActive && sortConfig.direction === 'asc';
    const isDescending = isActive && sortConfig.direction === 'desc';

    return (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            {/* Up arrow - faded when descending */}
            <path
                d="M8.59334 5.11111C8.9566 5.11111 9.13824 4.72222 8.87588 4.48148L6.29263 2.11111C6.13118 1.96296 5.86882 1.96296 5.70736 2.11111L3.12412 4.48148C2.86176 4.72222 2.86176 5.11111 3.40666 5.11111H8.59334Z"
                fill={isDescending ? "#ADB5BD" : "#6C757D"} // Faded when descending
            />
            {/* Down arrow - faded when ascending */}
            <path
                d="M3.40666 6.88889C3.0434 6.88889 2.86176 7.27777 3.12412 7.51851L5.70736 9.88889C5.86882 10.037 6.13118 10.037 6.29264 9.88889L8.87588 7.51851C9.13824 7.27777 8.9566 6.88889 8.59334 6.88889H3.40666Z"
                fill={isAscending ? "#ADB5BD" : "#6C757D"} // Faded when ascending
            />
        </svg>
    );
};

// Animated Arrow Icon Component for Parent Category dropdown
const AnimatedArrowIcon = ({ isOpen }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        style={{
            transition: 'transform 0.3s ease',
            transform: isOpen ? 'rotate(0deg)' : 'rotate(180deg)'
        }}
    >
        <path
            d="M4.5 11.25L9 6.75L13.5 11.25"
            stroke="#212529"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// Filter icon components for reusability
const FilterActiveIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333" stroke="#0D6EFD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <g clipPath="url(#clip0_4516_4830)">
            <path d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z" fill="#0D6EFD" />
        </g>
        <defs>
            <clipPath id="clip0_4516_4830">
                <rect width="12" height="12" fill="white" transform="translate(11 5)" />
            </clipPath>
        </defs>
    </svg>
);

const FilterInactiveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export default function Category() {
    const [entities, setEntities] = useState([]);

    // Add state for deactivate confirmation modal
    const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [categoryToDeactivate, setCategoryToDeactivate] = useState(null);
    const [deactivateActionType, setDeactivateActionType] = useState('deactivate'); // 'deactivate' or 'activate'

    const handleToggleStatus = async (row) => {
        setDropdownOpenId(null);
    };

    const [activeTab, setActiveTab] = useState('categories');
    const history = useHistory();
    const location = useLocation();
    const [categoriesData, setCategoriesData] = useState([]);
    const [previousCategoryId, setPreviousCategoryId] = useState(null);
    const [navigationStack, setNavigationStack] = useState([]);

    const breadcrumbs = ["Product", "Category"];

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedIds, setSelectedIds] = useState([]);
    const [archivedSearchTerm, setArchivedSearchTerm] = useState('');
    const [archivedCurrentPage, setArchivedCurrentPage] = useState(1);
    const [archivedSelectedIds, setarchivedSelectedIds] = useState([]);

    const [suppressHighlight, setSuppressHighlight] = useState(false);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerTab, setDrawerTab] = useState('general-info');
    const [selectedRow, setSelectedRow] = useState(null);

    const [categories, setCategories] = useState([]);
    const [archivedCategories, setArchivedCategories] = useState([]);

    const [searchStatus, setSearchStatus] = useState([]);
    const [searchParentCategory, setSearchParentCategory] = useState([]);
    const [parentCategorySearchTerm, setParentCategorySearchTerm] = useState('');

    // Temporary filter states for categories
    const [tempSearchStatus, setTempSearchStatus] = useState([]);
    const [tempSearchParentCategory, setTempSearchParentCategory] = useState([]);
    const [tempParentCategorySearchTerm, setTempParentCategorySearchTerm] = useState('');

    const filterButtonRef = useRef(null);
    const filterPopupRef = useRef(null);
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });

    const archivedFilterButtonRef = useRef(null);
    const archivedFilterPopupRef = useRef(null);
    const [archivedFilterPopupOpen, setArchivedFilterPopupOpen] = useState(false);
    const [archivedFilterPosition, setArchivedFilterPosition] = useState({ top: 0, left: 0 });
    const [archivedSearchStatus, setArchivedSearchStatus] = useState([]);
    const [archivedSearchParentCategory, setArchivedSearchParentCategory] = useState([]);
    const [archivedParentCategorySearchTerm, setArchivedParentCategorySearchTerm] = useState('');

    // Temporary filter states for archived categories
    const [tempArchivedSearchStatus, setTempArchivedSearchStatus] = useState([]);
    const [tempArchivedSearchParentCategory, setTempArchivedSearchParentCategory] = useState([]);
    const [tempArchivedParentCategorySearchTerm, setTempArchivedParentCategorySearchTerm] = useState('');

    // State for parent category dropdown visibility
    const [parentCategoryDropdownOpen, setParentCategoryDropdownOpen] = useState(false);
    const [archivedParentCategoryDropdownOpen, setArchivedParentCategoryDropdownOpen] = useState(false);

    // Refs for parent category dropdowns to handle click outside
    const parentCategoryDropdownRef = useRef(null);
    const archivedParentCategoryDropdownRef = useRef(null);

    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const categoryId = params.get("id");
    const autoOpen = params.get("autoOpen");

    // State for sorting
    const [sortConfig, setSortConfig] = useState({ key: 'modified_at', direction: 'desc' });
    const [archivedSortConfig, setArchivedSortConfig] = useState({ key: 'modified_at', direction: 'desc' });

    // Archive modal states
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [spuValidationModalOpen, setSpuValidationModalOpen] = useState(false);
    const [categoryToArchive, setCategoryToArchive] = useState(null);
    const [archiveMessage, setArchiveMessage] = useState('');
    const { showLoader, hideLoader } = useLoader();

    // Search states for Enter key functionality
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [archivedActiveSearchTerm, setArchivedActiveSearchTerm] = useState('');

    // Alert notification state - updated to match AlertNotification component structure
    const [alertNotification, setAlertNotification] = useState({
        show: false,
        type: 'success', // 'success' or 'error'
        recordName: '',
        action: '' // 'create', 'update', 'discard', 'activate', 'deactivate', 'archive'
    });

    // Filter applied states to track when filters are actually applied
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [archivedFiltersApplied, setArchivedFiltersApplied] = useState(false);
    const [categoryStack, setCategoryStack] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [archivedTotalCount, setArchivedTotalCount] = useState(0);
    const isFirstCategoryLoad = useRef(true);
    const isFirstCategoryFilterRender = useRef(true);
    const isFirstArchivedCategoryFilterRender = useRef(true);
    const catPrefetchCache = useRef(new Map());
    const catPrefetchInProgress = useRef(new Set());
    const archivedCatPrefetchCache = useRef(new Map());
    const archivedCatPrefetchInProgress = useRef(new Set());

    // NEW: State for dimmed background when filter drawer is open
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

    // Fetch all active categories once for filter dropdown options
    useEffect(() => {
        async function fetchAllCategories() {
            try {
                const res = await apiHelper('inventory/categories/?is_archived=false', 'GET');
                if (res.ok) {
                    const json = await res.json();
                    setCategoriesData(deduplicateById(json.results || json));
                }
            } catch (e) {
                console.error('Failed to fetch all categories for filter:', e);
            }
        }
        fetchAllCategories();
    }, []);

    // Handle click outside for parent category dropdowns
    useEffect(() => {
        function handleClickOutsideParentCategory(event) {
            // For categories filter
            if (parentCategoryDropdownOpen &&
                parentCategoryDropdownRef.current &&
                !parentCategoryDropdownRef.current.contains(event.target)) {
                setParentCategoryDropdownOpen(false);
            }

            // For archived categories filter
            if (archivedParentCategoryDropdownOpen &&
                archivedParentCategoryDropdownRef.current &&
                !archivedParentCategoryDropdownRef.current.contains(event.target)) {
                setArchivedParentCategoryDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutsideParentCategory);
        return () => document.removeEventListener('mousedown', handleClickOutsideParentCategory);
    }, [parentCategoryDropdownOpen, archivedParentCategoryDropdownOpen]);

    // Handle Enter key for Categories search
    const handleCategoriesSearch = (e) => {
        if (e.key === 'Enter') {
            setActiveSearchTerm(searchTerm);
            setCurrentPage(1);
        }
    };

    // Handle Enter key for Archived Categories search - FIXED
    const handleArchivedCategoriesSearch = (e) => {
        if (e.key === 'Enter') {
            setArchivedActiveSearchTerm(archivedSearchTerm);
            setArchivedCurrentPage(1);
        }
    };

    // Handle Categories search input change
    const handleCategoriesSearchChange = (e) => {
        setSearchTerm(e.target.value);
        // Clear active search term when user starts typing new search
        if (activeSearchTerm && e.target.value !== activeSearchTerm) {
            setActiveSearchTerm('');
        }
    };

    // Handle Archived Categories search input change - FIXED
    const handleArchivedCategoriesSearchChange = (e) => {
        setArchivedSearchTerm(e.target.value);
        // Clear active search term when user starts typing new search
        if (archivedActiveSearchTerm && e.target.value !== archivedActiveSearchTerm) {
            setArchivedActiveSearchTerm('');
        }
    };

    // Clear search term for categories
    const handleClearCategoriesSearch = () => {
        setSearchTerm('');
        setActiveSearchTerm('');
    };

    // Clear search term for archived categories
    const handleClearArchivedSearch = () => {
        setArchivedSearchTerm('');
        setArchivedActiveSearchTerm('');
    };

    // Handle sorting for Categories table
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Handle sorting for Archived Categories table
    const handleArchivedSort = (key) => {
        setArchivedSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Initialize temp states when filter opens
    useEffect(() => {
        if (filterPopupOpen) {
            setTempSearchStatus([...searchStatus]);
            setTempSearchParentCategory([...searchParentCategory]);
            setTempParentCategorySearchTerm(parentCategorySearchTerm);
            setParentCategoryDropdownOpen(false); // Close dropdown when filter opens
        }
    }, [filterPopupOpen]);

    // Initialize temp states when archived filter opens
    useEffect(() => {
        if (archivedFilterPopupOpen) {
            setTempArchivedSearchStatus([...archivedSearchStatus]);
            setTempArchivedSearchParentCategory([...archivedSearchParentCategory]);
            setTempArchivedParentCategorySearchTerm(archivedParentCategorySearchTerm);
            setArchivedParentCategoryDropdownOpen(false); // Close dropdown when filter opens
        }
    }, [archivedFilterPopupOpen]);

    // NEW: Handle filter button click with dimmed background
    const handleFilterButtonClick = () => {
        setFilterPopupOpen(true);
        setFilterDrawerOpen(true);
    };

    // NEW: Handle archived filter button click with dimmed background
    const handleArchivedFilterButtonClick = () => {
        setArchivedFilterPopupOpen(true);
        setFilterDrawerOpen(true);
    };

    // Handle filter apply for categories
    const handleApplyFilter = () => {
        setSearchStatus([...tempSearchStatus]);
        setSearchParentCategory([...tempSearchParentCategory]);
        setParentCategorySearchTerm(tempParentCategorySearchTerm);
        setFilterPopupOpen(false);
        setFilterDrawerOpen(false);
        setFiltersApplied(true);
    };

    // FIXED: Handle clear filter for categories - clear both temp and actual filter states
    const handleClearFilter = () => {
        setTempSearchStatus([]);
        setTempSearchParentCategory([]);
        setTempParentCategorySearchTerm('');
        setParentCategoryDropdownOpen(false);

        // ALSO CLEAR THE ACTUAL FILTER STATES
        setSearchStatus([]);
        setSearchParentCategory([]);
        setParentCategorySearchTerm('');
        setFiltersApplied(false);
    };

    // Handle filter apply for archived categories
    const handleArchivedApplyFilter = () => {
        setArchivedSearchStatus([...tempArchivedSearchStatus]);
        setArchivedSearchParentCategory([...tempArchivedSearchParentCategory]);
        setArchivedParentCategorySearchTerm(tempArchivedParentCategorySearchTerm);
        setArchivedFilterPopupOpen(false);
        setFilterDrawerOpen(false);
        setArchivedFiltersApplied(true);
    };

    // FIXED: Handle clear filter for archived categories - clear both temp and actual filter states
    const handleArchivedClearFilter = () => {
        setTempArchivedSearchStatus([]);
        setTempArchivedSearchParentCategory([]);
        setTempArchivedParentCategorySearchTerm('');
        setArchivedParentCategoryDropdownOpen(false);

        // ALSO CLEAR THE ACTUAL FILTER STATES
        setArchivedSearchStatus([]);
        setArchivedSearchParentCategory([]);
        setArchivedParentCategorySearchTerm('');
        setArchivedFiltersApplied(false);
    };

    // NEW: Handle closing filter drawer (when clicking outside or cancel)
    const handleCloseFilterDrawer = () => {
        setFilterPopupOpen(false);
        setArchivedFilterPopupOpen(false);
        setFilterDrawerOpen(false);
    };

    // Deduplicate data by category_id
    const deduplicateById = (data) => {
        const seen = new Set();
        return data.filter(item => {
            if (!item.category_id) return false;
            if (seen.has(item.category_id)) return false;
            seen.add(item.category_id);
            return true;
        });
    };

    // Server handles filtering/sorting — categories already contains the current page
    const filtered = categories;
    // Server handles filtering/sorting — archivedCategories already contains the current page
    const archivedFiltered = archivedCategories;

    // ── Prefetch helpers ──────────────────────────────────────────────────────
    function buildCatCacheKey(page, search, statuses, parentCats, sortKey, sortDir, isArchived) {
        return JSON.stringify({ page, search: search.trim(), statuses, parentCats, sortKey, sortDir, isArchived });
    }

    function catOrderingParam(sortKey, sortDir) {
        if (!sortKey) return '-modified_at';
        const prefix = sortDir === 'desc' ? '-' : '';
        return `${prefix}${sortKey}`;
    }

    async function prefetchCatPage(page, search, statuses, parentCats, sortKey, sortDir, isArchived, count, cacheRef, inProgressRef) {
        const totalPages = Math.ceil(count / PAGE_SIZE);
        if (page > totalPages) return;
        const key = buildCatCacheKey(page, search, statuses, parentCats, sortKey, sortDir, isArchived);
        if (cacheRef.current.has(key) || inProgressRef.current.has(key)) return;
        inProgressRef.current.add(key);
        try {
            const params = new URLSearchParams({ is_archived: isArchived ? 'true' : 'false', page, page_size: PAGE_SIZE });
            if (search.trim()) params.set('search', search.trim());
            if (statuses.length) params.set('status_filter', statuses.join(','));
            if (parentCats.length) params.set('parent_category', parentCats.join(','));
            params.set('ordering', catOrderingParam(sortKey, sortDir));
            const res = await apiHelper(`inventory/categories/?${params}`, 'GET');
            if (res.ok) {
                const json = await res.json();
                cacheRef.current.set(key, { results: deduplicateById(json.results || []), count: json.count || 0 });
            }
        } catch (_) { /* silent */ } finally { inProgressRef.current.delete(key); }
    }
    // ── End prefetch helpers ──────────────────────────────────────────────────

    // Reset active page when filters/sort change
    useEffect(() => {
        if (isFirstCategoryFilterRender.current) { isFirstCategoryFilterRender.current = false; return; }
        setCurrentPage(1);
        catPrefetchCache.current.clear();
        catPrefetchInProgress.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeSearchTerm, searchStatus, searchParentCategory, sortConfig]);

    // Reset archived page when archived filters/sort change
    useEffect(() => {
        if (isFirstArchivedCategoryFilterRender.current) { isFirstArchivedCategoryFilterRender.current = false; return; }
        setArchivedCurrentPage(1);
        archivedCatPrefetchCache.current.clear();
        archivedCatPrefetchInProgress.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [archivedActiveSearchTerm, archivedSearchStatus, archivedSearchParentCategory, archivedSortConfig]);

    // Server-side fetch — active categories
    useEffect(() => {
        async function loadCategories() {
            const key = buildCatCacheKey(currentPage, activeSearchTerm, searchStatus, searchParentCategory, sortConfig.key, sortConfig.direction, false);
            const cached = catPrefetchCache.current.get(key);
            if (cached) {
                setCategories(cached.results);
                setTotalCount(cached.count);
                prefetchCatPage(currentPage + 1, activeSearchTerm, searchStatus, searchParentCategory, sortConfig.key, sortConfig.direction, false, cached.count, catPrefetchCache, catPrefetchInProgress);
                return;
            }
            const isInitial = isFirstCategoryLoad.current;
            if (isInitial) { showLoader(); isFirstCategoryLoad.current = false; }
            try {
                const params = new URLSearchParams({ is_archived: 'false', page: currentPage, page_size: PAGE_SIZE });
                if (activeSearchTerm.trim()) params.set('search', activeSearchTerm.trim());
                if (searchStatus.length) params.set('status_filter', searchStatus.join(','));
                if (searchParentCategory.length) params.set('parent_category', searchParentCategory.join(','));
                params.set('ordering', catOrderingParam(sortConfig.key, sortConfig.direction));
                const res = await apiHelper(`inventory/categories/?${params}`, 'GET');
                if (res.ok) {
                    const json = await res.json();
                    const results = deduplicateById(json.results || []);
                    setCategories(results);
                    setTotalCount(json.count || 0);
                    prefetchCatPage(currentPage + 1, activeSearchTerm, searchStatus, searchParentCategory, sortConfig.key, sortConfig.direction, false, json.count || 0, catPrefetchCache, catPrefetchInProgress);
                } else {
                    console.error('Failed to fetch categories');
                }
            } catch (e) {
                console.error('Error fetching categories:', e);
            } finally {
                if (isInitial) hideLoader();
            }
        }
        loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, activeSearchTerm, searchStatus, searchParentCategory, sortConfig]);

    // Server-side fetch — archived categories
    useEffect(() => {
        async function loadArchivedCategories() {
            const key = buildCatCacheKey(archivedCurrentPage, archivedActiveSearchTerm, archivedSearchStatus, archivedSearchParentCategory, archivedSortConfig.key, archivedSortConfig.direction, true);
            const cached = archivedCatPrefetchCache.current.get(key);
            if (cached) {
                setArchivedCategories(cached.results);
                setArchivedTotalCount(cached.count);
                prefetchCatPage(archivedCurrentPage + 1, archivedActiveSearchTerm, archivedSearchStatus, archivedSearchParentCategory, archivedSortConfig.key, archivedSortConfig.direction, true, cached.count, archivedCatPrefetchCache, archivedCatPrefetchInProgress);
                return;
            }
            try {
                const params = new URLSearchParams({ is_archived: 'true', page: archivedCurrentPage, page_size: PAGE_SIZE });
                if (archivedActiveSearchTerm.trim()) params.set('search', archivedActiveSearchTerm.trim());
                if (archivedSearchStatus.length) params.set('status_filter', archivedSearchStatus.join(','));
                if (archivedSearchParentCategory.length) params.set('parent_category', archivedSearchParentCategory.join(','));
                params.set('ordering', catOrderingParam(archivedSortConfig.key, archivedSortConfig.direction));
                const res = await apiHelper(`inventory/categories/?${params}`, 'GET');
                if (res.ok) {
                    const json = await res.json();
                    const results = deduplicateById(json.results || []);
                    setArchivedCategories(results);
                    setArchivedTotalCount(json.count || 0);
                    prefetchCatPage(archivedCurrentPage + 1, archivedActiveSearchTerm, archivedSearchStatus, archivedSearchParentCategory, archivedSortConfig.key, archivedSortConfig.direction, true, json.count || 0, archivedCatPrefetchCache, archivedCatPrefetchInProgress);
                }
            } catch (e) {
                console.error('Error fetching archived categories:', e);
            }
        }
        loadArchivedCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [archivedCurrentPage, archivedActiveSearchTerm, archivedSearchStatus, archivedSearchParentCategory, archivedSortConfig]);

    // fetchCategories / fetchArchivedCategories removed — replaced by server-side paginated useEffects above.
    // Keeping a thin helper used by archive/deactivate mutations to refresh data:
    const refreshCategories = () => {
        catPrefetchCache.current.clear();
        catPrefetchInProgress.current.clear();
        archivedCatPrefetchCache.current.clear();
        archivedCatPrefetchInProgress.current.clear();
        setCurrentPage(1);
        setArchivedCurrentPage(1);
    };

    useEffect(() => {
        const fetchData = async () => {
            // initial load is now handled by the server-side fetch useEffects above
            return;
        };

        fetchData();
    }, []);

    // Fix for handling success messages from navigation
    useEffect(() => {
        console.log('location.state:', location.state);

        if (location && location.state && location.state.success) {
            // Handle navigation success messages
            setAlertNotification({
                show: true,
                type: 'success',
                recordName: 'Category',
                action: location.state.action || 'update' // Default to update if no specific action
            });
            history.replace(location.pathname, {});
        }
    }, [location, history]);

    // Separate useEffect for auto-clearing alert notifications
    useEffect(() => {
        if (alertNotification.show) {
            const t = setTimeout(() => {
                setAlertNotification(prev => ({ ...prev, show: false }));
            }, 5000); // Match the 5-second timeout from AlertNotification
            return () => clearTimeout(t);
        }
    }, [alertNotification.show]);

    // Reset page numbers when filters or search terms change
    useEffect(() => {
        setCurrentPage(1);
    }, [activeSearchTerm, searchStatus, searchParentCategory]);

    useEffect(() => {
        setArchivedCurrentPage(1);
    }, [archivedActiveSearchTerm, archivedSearchStatus, archivedSearchParentCategory]);

    // Pagination Logic
    const getPaginatedData = (data, page) => {
        const startIndex = (page - 1) * PAGE_SIZE;
        const endIndex = startIndex + PAGE_SIZE;
        return data.slice(startIndex, endIndex);
    };

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const paginatedCategories = categories;        // server already returned current page

    const archivedTotalPages = Math.max(1, Math.ceil(archivedTotalCount / PAGE_SIZE));
    const paginatedArchivedCategories = archivedCategories; // server already returned current page

    const toggleSelect = id => {
        setSelectedIds(ids => {
            const newIds = ids.includes(id)
                ? ids.filter(x => x !== id)
                : [...ids, id];
            setSuppressHighlight(false);
            return newIds;
        });
    };

    const toggleAll = () => {
        setSelectedIds(prev => {
            const next =
                prev.length === paginatedCategories.length
                    ? []
                    : paginatedCategories.map(d => d.category_id);
            setSuppressHighlight(false);
            return next;
        });
    };

    const onRowClick = (row) => {
        // Add current selected row to navigation stack if it exists
        setNavigationStack([row]);
        setSelectedRow(row);
        setDrawerTab('general-info');
        setDrawerOpen(true);
    };

    // New function to handle back navigation in drawer
    const handleDrawerBack = () => {
        setNavigationStack(prev => {
            if (prev.length <= 1) {
                setDrawerOpen(false);
                setSelectedRow(null);
                return [];
            }

            const newStack = prev.slice(0, -1);
            setSelectedRow(newStack[newStack.length - 1]);
            return newStack;
        });

        setDrawerTab('general-info');
    };

    const [dropdownOpenId, setDropdownOpenId] = useState(null);
    const dropdownRef = useRef(null);
    const buttonRefs = useRef({});
    const drawerRef = useRef(null);
    const currentRow = categories.find(e => e.category_id === dropdownOpenId);

    // FIXED: Modified handleClickOutsideDrawer to prevent closing when modal actions are performed
    useEffect(() => {
        function handleClickOutsideDrawer(event) {
            // Don't close drawer if deactivate or archive modals are open
            if (deactivateModalOpen || archiveModalOpen || spuValidationModalOpen) {
                return;
            }

            if (
                drawerOpen &&
                drawerRef.current &&
                !drawerRef.current.contains(event.target)
            ) {
                setDrawerOpen(false);
                setSelectedRow(null);
                setSelectedIds(ids =>
                    selectedRow ? ids.filter(id => id !== selectedRow.external_id) : ids
                );
                setNavigationStack([]);
            }
        }
        document.addEventListener('mousedown', handleClickOutsideDrawer);
        return () =>
            document.removeEventListener('mousedown', handleClickOutsideDrawer);
    }, [drawerOpen, selectedRow, deactivateModalOpen, archiveModalOpen, spuValidationModalOpen]);

    useEffect(() => {
        function handleClickOutsideDropdown(event) {
            if (
                dropdownOpenId !== null &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                !buttonRefs.current[dropdownOpenId]?.contains(event.target)
            ) {
                setDropdownOpenId(null);
            }
        }
        document.addEventListener('mousedown', handleClickOutsideDropdown);
        return () => document.removeEventListener('mousedown', handleClickOutsideDropdown);
    }, [dropdownOpenId]);

    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    const toggleDropdown = (id) => {
        if (dropdownOpenId === id) {
            setDropdownOpenId(null);
        } else {
            const btn = buttonRefs.current[id];
            if (btn) {
                const rect = btn.getBoundingClientRect();
                const dropdownWidth = 150;
                const dropdownHeight = 100;

                let left = rect.left + window.scrollX - dropdownWidth + rect.width;
                if (left < window.scrollX + 8) {
                    left = window.scrollX + 8;
                }

                let top = rect.bottom + window.scrollY;
                if (top + dropdownHeight > window.scrollY + window.innerHeight) {
                    top = rect.top + window.scrollY - dropdownHeight;
                }

                setDropdownPosition({ top, left });
            }
            setDropdownOpenId(id);
        }
    };

    const archivedRef = useRef(null);
    const [archivedModalOpen, setArchivedModalOpen] = useState(false);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [selectedCategoriesId, setSelectedCategoriesId] = useState(null);
    const [validationError, setValidationError] = useState([]);

    // New Archive Functions
    const openArchiveModal = (categoryId, categoryName = '') => {
        setCategoryToArchive(categoryId);
        setArchiveModalOpen(true);
    };

    // FIXED: Modified handleArchiveConfirm to keep drawer open
    const handleArchiveConfirm = async (categoryId) => {
        if (!categoryId) {
            console.error("No category ID selected");
            return;
        }

        try {
            const response = await apiHelper(`inventory/categories/${categoryId}/check-archive/`, 'POST');

            if (response.ok) {
                const data = await response.json();

                setAlertNotification({
                    show: true,
                    type: 'success',
                    recordName: 'Category',
                    action: 'archive'
                });

                // Get the category name before removing it
                const archivedCategoryName = categories.find(cat => cat.category_id === categoryId)?.category_name;

                // 1. Remove the archived category
                setCategories(prev => prev.filter(cat => cat.category_id !== categoryId));

                // 2. Update all child categories to remove the archived parent reference
                setCategories(prev => prev.map(cat => {
                    if (cat.parent_name === archivedCategoryName) {
                        return {
                            ...cat,
                            parent_name: null,
                            parent_parent_name: null
                        };
                    }
                    return cat;
                }));

                // 3. Also update archived categories if any child was already archived
                setArchivedCategories(prev => prev.map(cat => {
                    if (cat.parent_name === archivedCategoryName) {
                        return {
                            ...cat,
                            parent_name: null,
                            parent_parent_name: null
                        };
                    }
                    return cat;
                }));

                // 4. Update filtered data for immediate UI update
                // This updates the child categories in the current view
                // We need to update the filtered state as well
                const updatedCategories = categories
                    .filter(cat => cat.category_id !== categoryId)
                    .map(cat => {
                        if (cat.parent_name === archivedCategoryName) {
                            return {
                                ...cat,
                                parent_name: null,
                                parent_parent_name: null
                            };
                        }
                        return cat;
                    });

                // This will trigger a re-render with updated data
                // Note: categories state will be updated by setCategories above,
                // but we also need to ensure filtered state gets the updated data

                // 5. Update the selected row if it's a child of the archived category
                if (selectedRow && selectedRow.parent_name === archivedCategoryName) {
                    setSelectedRow(prev => ({
                        ...prev,
                        parent_name: null,
                        parent_parent_name: null
                    }));
                }

                // 6. If the drawer is open for a child category, update its parent display
                // This ensures the drawer shows the updated parent info
                if (drawerOpen && selectedRow && selectedRow.parent_name === archivedCategoryName) {
                    // The drawer will re-render with the updated selectedRow
                    // No additional action needed as selectedRow is already updated above
                }

                setTotalCount(prev => Math.max(0, prev - 1));
                refreshCategories();

                setArchiveModalOpen(false);
                setCategoryToArchive(null);

            } else {
                const errorData = await response.json();
                if (errorData.error && errorData.linked_spus_count > 0) {
                    setArchiveMessage(errorData.error);
                    setSpuValidationModalOpen(true);
                    setArchiveModalOpen(false);
                } else {
                    console.error("Error archiving category:", errorData);
                    setAlertNotification({
                        show: true,
                        type: 'error',
                        recordName: 'Category',
                        action: 'archive'
                    });
                    setArchiveModalOpen(false);
                }
            }
        } catch (err) {
            console.error("Error archiving category:", err);
            setAlertNotification({
                show: true,
                type: 'error',
                recordName: 'Category',
                action: 'archive'
            });
            setArchiveModalOpen(false);
        }
    };
    // CHANGED: Modified activate function to show toast instead of confirmation modal
    const handleActivateCategory = async (categoryId) => {
        if (!categoryId) {
            console.error("No category ID found");
            return;
        }

        try {
            const response = await apiHelper(`inventory/categories/${categoryId}/deactivate/`, 'POST');

            if (response.ok) {
                const data = await response.json();
                console.log("Category activated successfully:", data);

                const isActive = data.status;
                const newStatusText = isActive ? "Active" : "Inactive";

                setAlertNotification({
                    show: true,
                    type: 'success',
                    recordName: 'Category',
                    action: 'activate'
                });

                setCategories(prev => prev.map(cat =>
                    cat.category_id === categoryId
                        ? { ...cat, status: isActive, status_text: newStatusText }
                        : cat
                ));

                setSelectedRow(prev =>
                    prev && prev.category_id === categoryId
                        ? { ...prev, status: isActive, status_text: newStatusText }
                        : prev
                );

                setDropdownOpenId(null);
                setToolbarDropdownOpen(false);

                // Keep drawer open - no changes needed here
            } else {
                const error = await response.json();
                console.error("Error activating category:", error);
                setAlertNotification({
                    show: true,
                    type: 'error',
                    recordName: 'Category',
                    action: 'activate'
                });
            }
        } catch (err) {
            console.error("Network/server error while activating category:", err);
            setAlertNotification({
                show: true,
                type: 'error',
                recordName: 'Category',
                action: 'activate'
            });
        }
    };

    // Updated deactivate function with confirmation modal
    const openDeactivateModal = (categoryId, actionType = 'deactivate') => {
        // CHANGED: If activating, don't show confirmation modal, just activate directly
        if (actionType === 'activate') {
            handleActivateCategory(categoryId);
            return;
        }

        // For deactivate, show confirmation modal
        setCategoryToDeactivate(categoryId);
        setDeactivateActionType(actionType);
        setDeactivateModalOpen(true);
    };

    // FIXED: Modified handleDeactivateConfirm to keep drawer open
    const handleDeactivateConfirm = async () => {
        if (!categoryToDeactivate) {
            console.error("No category ID found");
            return;
        }

        try {
            const response = await apiHelper(`inventory/categories/${categoryToDeactivate}/deactivate/`, 'POST');

            if (response.ok) {
                const data = await response.json();
                console.log("Category status toggled successfully:", data);

                const isActive = data.status;
                const newStatusText = isActive ? "Active" : "Inactive";

                setAlertNotification({
                    show: true,
                    type: 'success',
                    recordName: 'Category',
                    action: deactivateActionType
                });

                setCategories(prev => prev.map(cat =>
                    cat.category_id === categoryToDeactivate
                        ? { ...cat, status: isActive, status_text: newStatusText }
                        : cat
                ));

                setSelectedRow(prev =>
                    prev && prev.category_id === categoryToDeactivate
                        ? { ...prev, status: isActive, status_text: newStatusText }
                        : prev
                );

                setDeactivateModalOpen(false);
                setCategoryToDeactivate(null);
                setDropdownOpenId(null);
                setToolbarDropdownOpen(false);

                // FIXED: Don't close the drawer - keep it open
                // Removed the code that was closing the drawer
            } else {
                const error = await response.json();
                console.error("Error toggling category status:", error);
                setAlertNotification({
                    show: true,
                    type: 'error',
                    recordName: 'Category',
                    action: deactivateActionType
                });
                setDeactivateModalOpen(false);
            }
        } catch (err) {
            console.error("Network/server error while toggling category status:", err);
            setAlertNotification({
                show: true,
                type: 'error',
                recordName: 'Category',
                action: deactivateActionType
            });
            setDeactivateModalOpen(false);
        }
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (archivedRef.current && !archivedRef.current.contains(event.target)) {
                setArchivedModalOpen(false);
            }
        }

        if (archivedModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [archivedModalOpen]);

    const editCategory = () => {
        setDropdownOpenId(null);

        // Priority 1: If we're in drawer context, use selectedRow
        if (selectedRow && selectedRow.category_id) {
            history.push(`/category/edit-category/${selectedRow.category_id}`);
            return;
        }

        // Priority 2: If single item is selected in table
        if (selectedIds.length === 1) {
            history.push(`/category/edit-category/${selectedIds[0]}`);
            return;
        }

        // Priority 3: If dropdown is open
        if (dropdownOpenId) {
            history.push(`/category/edit-category/${dropdownOpenId}`);
            return;
        }
    };

    const [toolbarDropdownOpen, setToolbarDropdownOpen] = useState(false);
    const toolbarRef = useRef(null);
    const toggleToolbarDropdown = () => {
        setToolbarDropdownOpen(prev => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
                setToolbarDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const handleCheckboxChange = (setter, value) => (e) => {
        const isChecked = e.target.checked;
        setter(prev =>
            isChecked
                ? [...prev, value]
                : prev.filter(v => v !== value)
        );
    };

    const handleArchivedCheckboxChange = (setter, value) => (e) => {
        const isChecked = e.target.checked;
        setter(prev =>
            isChecked
                ? [...prev, value]
                : prev.filter(v => v !== value)
        );
    };

    useEffect(() => {
        if (filterPopupOpen && filterButtonRef.current) {
            const rect = filterButtonRef.current.getBoundingClientRect();
            setFilterPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX
            });
        }
    }, [filterPopupOpen]);

    useEffect(() => {
        if (archivedFilterPopupOpen && archivedFilterButtonRef.current) {
            const rect = archivedFilterButtonRef.current.getBoundingClientRect();
            setArchivedFilterPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX
            });
        }
    }, [archivedFilterPopupOpen]);

    useEffect(() => {
        function handleClickOutsideFilter(event) {
            if (
                filterPopupOpen &&
                filterPopupRef.current &&
                !filterPopupRef.current.contains(event.target) &&
                !filterButtonRef.current.contains(event.target)
            ) {
                handleCloseFilterDrawer();
            }
            if (
                archivedFilterPopupOpen &&
                archivedFilterPopupRef.current &&
                !archivedFilterPopupRef.current.contains(event.target) &&
                !archivedFilterButtonRef.current.contains(event.target)
            ) {
                handleCloseFilterDrawer();
            }
        }
        document.addEventListener('mousedown', handleClickOutsideFilter);
        return () => document.removeEventListener('mousedown', handleClickOutsideFilter);
    }, [filterPopupOpen, archivedFilterPopupOpen]);

    const filterCount = [...searchStatus, ...searchParentCategory].filter(Boolean).length;
    const archivedFilterCount = [...archivedSearchStatus, ...archivedSearchParentCategory].filter(Boolean).length;

    const handleParentCategoryClick = (parentName) => {
        const parentCategory =
            categories.find(cat => cat.category_name === parentName) ||
            archivedCategories.find(cat => cat.category_name === parentName);
        if (!parentCategory) return;
        setNavigationStack(prev => [...prev, parentCategory]); // ✅ push parent
        setSelectedRow(parentCategory);
        setDrawerTab('general-info');
    };

    const openIdRef = useRef(null);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        if (!openIdRef.current) {
            console.error('openIdRef is not set');
            setAlertNotification({
                show: true,
                type: 'error',
                recordName: 'ID',
                action: 'copy'
            });
            return;
        }

        const textToCopy = openIdRef.current.innerText;
        if (!textToCopy) {
            console.error('No text to copy');
            setAlertNotification({
                show: true,
                type: 'error',
                recordName: 'ID',
                action: 'copy'
            });
            return;
        }

        if (window.isSecureContext && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
            try {
                await navigator.clipboard.writeText(textToCopy);
                setCopied(true);
                setTimeout(() => setCopied(false), 1000);
            } catch (err) {
                console.error('Clipboard API failed:', err);
                fallbackCopyTextToClipboard(textToCopy);
            }
        } else {
            console.warn('Clipboard API not available or not in secure context, using fallback');
            fallbackCopyTextToClipboard(textToCopy);
        }
    };

    const fallbackCopyTextToClipboard = (text) => {
        try {
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.top = '0';
            textArea.style.left = '0';
            textArea.style.opacity = '0';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            const successful = document.execCommand('copy');
            document.body.removeChild(textArea);

            if (successful) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1000);
            } else {
                console.error('Fallback copy failed: execCommand did not work');
                setAlertNotification({
                    show: true,
                    type: 'error',
                    recordName: 'ID',
                    action: 'copy'
                });
            }
        } catch (err) {
            console.error('Fallback copy failed:', err);
            setAlertNotification({
                show: true,
                type: 'error',
                recordName: 'ID',
                action: 'copy'
            });
        }
    };

    const handleBackClick = () => {
        if (previousCategoryId) {
            const previousCategory = categories.find(cat => cat.category_id === previousCategoryId);
            if (previousCategory) {
                setSelectedRow(previousCategory);
                setDrawerTab('general-info');
                setDrawerOpen(true);
            } else {
                setDrawerOpen(false);
                setSelectedRow(null);
            }
        } else {
            setDrawerOpen(false);
            setSelectedRow(null);
        }
    };

    const saveRef = useRef(null);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const handleSaveConfirm = () => {
        setSaveModalOpen(false);
    };

    const formatDateTime = (isoString) => {
        const date = new Date(isoString);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");

        return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    useEffect(() => {
        function handleClickOutside(event) {
            if (saveRef.current && !saveRef.current.contains(event.target)) {
                setSaveModalOpen(false);
            }
        }

        if (saveModalOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [saveModalOpen]);

    const [hasAutoOpened, setHasAutoOpened] = useState(false);

    useEffect(() => {
        if (!autoOpen || !categoryId || hasAutoOpened) return;

        const row =
            paginatedCategories.find(d => d.category_id === Number(categoryId)) ||
            paginatedArchivedCategories.find(d => d.category_id === Number(categoryId));

        if (row) {
            onRowClick(row);
            setHasAutoOpened(true);
            return;
        }

        // 2️⃣ fallback: search from FULL categories list (already fetched)
        if (categories && categories.length > 0) {
            const fullRow = categories.find(
                d => d.category_id === Number(categoryId)
            );

            if (fullRow) {
                onRowClick(fullRow);
                setHasAutoOpened(true);
            }
        }

    }, [
        autoOpen,
        categoryId,
        paginatedCategories,
        paginatedArchivedCategories,
        categories,
        hasAutoOpened,
        onRowClick
    ]);



    // Highlight matching text function - UPDATED to remove blue color styling
    const highlightMatch = (text, searchTerm) => {
        if (!searchTerm || !text) return text;
        // Return text as is without any styling
        return text;
    };

    // All categories from DB mapped to filter item shape {parent_name, parent_parent_name}
    const allCategoriesForFilter = categoriesData
        .map(c => ({
            parent_name: c.category_name,
            parent_parent_name: c.parent_name || null,
        }))
        .sort((a, b) => (a.parent_name || '').localeCompare(b.parent_name || ''));

    // Get unique parent categories for filter dropdown
    const getUniqueParentCategories = (categoriesList) => {
        const uniqueParents = [
            ...new Set(
                categoriesList
                    .filter(d => d?.parent_name && d?.parent_name?.trim() !== "")
                    .map(d => JSON.stringify({ parent_name: d.parent_name, parent_parent_name: d.parent_parent_name }))
            )
        ].map(str => JSON.parse(str));

        // Check if any record has NA or missing parent name
        const hasNA = categoriesList.some(
            d => !d.parent_name || d.parent_name.trim() === "" || d.parent_name.trim().toLowerCase() === "na"
        );

        // Add NA manually if missing
        if (hasNA && !uniqueParents.some(p => p.parent_name === "NA")) {
            uniqueParents.unshift({ parent_name: "NA", parent_parent_name: null });
        }

        return uniqueParents;
    };

    // FIXED: Function to handle parent category selection - clears search term
    const handleParentCategorySelect = (parentName, isSelected, setSearchParentCategory, setSearchTerm) => {
        const newSelection = isSelected
            ? setSearchParentCategory(prev => prev.filter(p => p !== parentName))
            : setSearchParentCategory(prev => [...prev, parentName]);

        // Clear the search term after selection
        setSearchTerm("");
    };

    // FIXED: Function to handle archived parent category selection - clears search term
    const handleArchivedParentCategorySelect = (parentName, isSelected, setSearchParentCategory, setSearchTerm) => {
        const newSelection = isSelected
            ? setSearchParentCategory(prev => prev.filter(p => p !== parentName))
            : setSearchParentCategory(prev => [...prev, parentName]);

        // Clear the search term after selection
        setSearchTerm("");
    };

    // Function to check if category name is overflowing
    const isCategoryNameOverflowing = (categoryName) => {
        if (!categoryName) return false;
        // Consider category name lengthy if it's more than 30 characters
        return categoryName.length > 30;
    };

    // Function to check if ID is overflowing
    const isIdOverflowing = (id) => {
        if (!id) return false;
        // Consider ID overflowing if it's more than 15 characters
        return String(id).length > 15;
    };

    const categoriesColumns = [
        {
            field: 'category_name',
            label: 'Category Name',
            sortable: true,
            width: '15%',
            render: (row) => (
                <div style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                }}>
                    {row.category_name}
                </div>
            )
        },
        {
            field: 'status_text',
            label: 'Status',
            sortable: true,
            width: '13%',
        },
        {
            field: 'external_id',
            label: 'ID',
            sortable: true,
            width: '13%',
        },
        {
            field: 'parent_name',
            label: 'Parent Category',
            sortable: true,
            width: '18%',
            render: (row) => (
                <div style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                }}>
                    {row.parent_name || <span style={{ color: '#ADB5BD' }}>NA</span>}
                </div>
            )
        },
        {
            field: 'prefix',
            label: 'Prefix',
            sortable: true,
            width: '15%',
        },
        {
            field: 'modified_at',
            label: 'Last Modified Time',
            sortable: true,
            width: '18%',
            render: (row) => formatDateTime(row.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>
        }
    ];


    const archivedColumns = [
        {
            field: 'category_name',
            label: 'Category Name',
            sortable: true,
            render: (row) => (
                <div style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "block",
                }}>
                    {row.category_name}
                </div>
            )
        },
        {
            field: 'external_id',
            label: 'ID',
            sortable: true,
        },
        {
            field: 'parent_name',
            label: 'Parent Category',
            sortable: true,
        },
        {
            field: 'prefix',
            label: 'Prefix',
            sortable: true,
        },
        {
            field: 'archived_at',
            label: 'Archived Date',
            sortable: true,
            render: (row) => formatDateTime(row.archived_at) || <span style={{ color: '#ADB5BD' }}>NA</span>
        }
    ];

    // Handle action click for DynamicTable
    const handleActionClick = (categoryId) => {
        toggleDropdown(categoryId);
    };

    // Handle page change for Categories
    const handleCategoriesPageChange = (page) => {
        setCurrentPage(page);
    };

    // Handle page change for Archived Categories
    const handleArchivedCategoriesPageChange = (page) => {
        setArchivedCurrentPage(page);
    };

    return (
        <div className="company-body new-look">
            {/* NEW: Dimmed overlay when filter drawer is open */}
            {filterDrawerOpen && (
                <div
                    className="drawer-overlay"
                    style={{
                        zIndex: 1999,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        pointerEvents: 'auto'
                    }}
                    onClick={handleCloseFilterDrawer}
                />
            )}

            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => (
                    <span key={index} className="breadcrumb-item">
                        {crumb}
                        {index !== breadcrumbs.length - 1}
                    </span>
                ))}
                <>
                  
                </>
            </div>

            {/* AlertNotification Component - Updated to match component structure with higher z-index */}
            {alertNotification.show && (
                <AlertNotification
                    type={alertNotification.type}
                    recordName={alertNotification.recordName}
                    action={alertNotification.action}
                    onClose={() => setAlertNotification(prev => ({ ...prev, show: false }))}
                    style={{ zIndex: 1100 }} // Higher than drawer z-index
                />
            )}

            <div className="employee-tabs-container">
                <div className="tabs">
                    <button
                        className={`tab ${activeTab === 'categories' ? 'active' : ''}`}
                        onClick={() => setActiveTab('categories')}
                    >
                        Categories
                    </button>
                    <button
                        className={`tab ${activeTab === 'archived-categories' ? 'active' : ''}`}
                        onClick={() => setActiveTab('archived-categories')}
                    >
                        Archived Categories
                    </button>
                </div>

                {activeTab === 'categories' && (
                    <div className="tab-content" id="categories-content">
                        <div className="info-section">
                            <div className="info-card">
                                <div className="entities-tab">
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <div className="search-box">
                                                <Search size={20} />
                                                <input
                                                    type="text"
                                                    placeholder="Search…"
                                                    value={searchTerm}
                                                    onChange={handleCategoriesSearchChange}
                                                    onKeyDown={handleCategoriesSearch}
                                                />
                                                {searchTerm && (
                                                    <X
                                                        size={12}
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#000000ff',
                                                        }}
                                                        onClick={handleClearCategoriesSearch}
                                                    />
                                                )}
                                            </div>
                                            <button
                                                ref={filterButtonRef}
                                                className={`icon-btn p-2 filter-btn cat-filter-btn ${filtersApplied ? 'applied' : ''}`}
                                                onClick={handleFilterButtonClick}
                                                style={{
                                                    border: filtersApplied ? '1px solid #2C7BE5' : '1px solid #6C757D',
                                                    borderWidth: '1px',
                                                    color: filtersApplied ? '#2C7BE5' : '#6C757D',
                                                    background: filtersApplied ? '#CFE2FF' : '#ffffffff',
                                                    height: '40px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {filtersApplied ? (
                                                    <FilterActiveIcon />
                                                ) : (
                                                    <FilterInactiveIcon />
                                                )}
                                            </button>
                                        </div>
                                        <div className="toolbar-right">
                                            <button
                                                className="icon-btn dark-border"
                                                onClick={() => history.push("/category/add-category")}
                                            >
                                                <Plus size={18} /> Add Category
                                            </button>
                                        </div>
                                    </div>

                                    {filterPopupOpen && (
                                        <div
                                            ref={filterPopupRef}
                                            style={{
                                                position: "fixed",
                                                top: 0,
                                                right: 0,
                                                width: 360,
                                                height: "100vh",
                                                backgroundColor: "#fff",
                                                boxShadow: "-3px 0 12px rgba(0,0,0,0.1)",
                                                zIndex: 2000,
                                                display: "flex",
                                                flexDirection: "column",
                                                animation: "slideIn 0.3s ease-out",
                                            }}
                                        >
                                            {/* Header */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "16px 20px",
                                                    borderBottom: "1px solid #f1f1f1",
                                                }}
                                            >
                                                <h4 style={{ fontSize: '14px', margin: 0, fontWeight: 600, color: "#212529", fontFamily: 'Montserrat' }}>Filter</h4>
                                                <button
                                                    onClick={handleCloseFilterDrawer}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>

                                            {/* Body */}
                                            <div
                                                style={{
                                                    flex: 1,
                                                    overflowY: "auto",
                                                    padding: "20px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    // gap: "24px",
                                                }}
                                            >
                                                {/* Status Filter - CHANGED: Added black border for checked checkboxes */}
                                                <div>
                                                    <label
                                                        style={{
                                                            display: "block",
                                                            fontWeight: 600,
                                                            marginBottom: "10px",
                                                            color: "#212529",
                                                            fontFamily: 'Montserrat',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        Status
                                                    </label>
                                                    <div style={{ display: "flex", flexDirection: "column" }}>
                                                        {["Active", "Inactive"].map((status) => {
                                                            const isChecked = tempSearchStatus.includes(status);

                                                            return (
                                                                <label
                                                                    key={status}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        gap: "8px",
                                                                        cursor: "pointer",
                                                                        userSelect: "none",
                                                                        padding: '10px'
                                                                    }}
                                                                >
                                                                    {/* Hidden native checkbox */}
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => {
                                                                            const checked = e.target.checked;
                                                                            setTempSearchStatus((prev) =>
                                                                                checked ? [...prev, status] : prev.filter((v) => v !== status)
                                                                            );
                                                                        }}
                                                                        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
                                                                    />

                                                                    {/* Custom Checkbox */}
                                                                    <span
                                                                        style={{
                                                                            width: "18px",
                                                                            height: "18px",
                                                                            border: isChecked ? "1px solid #000000" : "1px solid #afafaf",
                                                                            borderRadius: "4px",
                                                                            backgroundColor: isChecked ? "#FFEB00" : "white",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            justifyContent: "center",
                                                                            transition: "all 0.2s ease",
                                                                            position: "relative",
                                                                            flexShrink: 0,
                                                                        }}
                                                                    >
                                                                        {isChecked && (
                                                                            <svg
                                                                                width="12"
                                                                                height="12"
                                                                                viewBox="0 0 12 12"
                                                                                fill="none"
                                                                                xmlns="http://www.w3.org/2000/svg"
                                                                                style={{ position: "absolute" }}
                                                                            >
                                                                                <path
                                                                                    d="M2 6L4.5 8.5L10 3"
                                                                                    stroke="#000"
                                                                                    strokeWidth="2"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                />
                                                                            </svg>
                                                                        )}
                                                                    </span>

                                                                    <span style={{ fontSize: "14px", color: '#212529', fontWeight: '400' }}>{status}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                                <div style={{ border: '1px solid #E9ECEF', margin: "20px 0px" }}></div>
                                                {/* Parent Category Filter - FIXED with up/down arrows and click outside handling */}
                                                <div ref={parentCategoryDropdownRef}>
                                                    <label
                                                        style={{
                                                            display: "block",
                                                            fontWeight: 600,
                                                            fontSize: '14px',
                                                            marginBottom: "10px",
                                                            color: "#000",
                                                        }}
                                                    >
                                                        Parent Category
                                                    </label>

                                                    {/* Search Input with Chips and Arrow */}
                                                    <div
                                                        className="search-box-filter"
                                                        style={{
                                                            position: "relative",
                                                            border: "1px solid #d9d9d9",
                                                            borderRadius: "8px",
                                                            padding: "4px 32px 4px 8px", // Increased right padding for arrow space
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            minHeight: "36px",
                                                        }}
                                                    >
                                                        {/* Selected Categories Chips */}
                                                        {tempSearchParentCategory.map((parent) => {
                                                            if (!parent) return null;
                                                            return (
                                                                <span
                                                                    key={parent}
                                                                    style={{
                                                                        backgroundColor: "#ffeb00",
                                                                        color: "#212529",
                                                                        borderRadius: "16px",
                                                                        padding: "4px 8px",
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        fontSize: "12px",
                                                                        fontWeight: "600",
                                                                        // whiteSpace: "nowrap",
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis'
                                                                    }}
                                                                >
                                                                    {parent}
                                                                    <button
                                                                        onClick={() => {
                                                                            const newSelection = tempSearchParentCategory.filter(p => p !== parent);
                                                                            setTempSearchParentCategory(newSelection);
                                                                            setTempParentCategorySearchTerm("");
                                                                        }}
                                                                        style={{
                                                                            background: "none",
                                                                            border: "none",
                                                                            marginLeft: "6px",
                                                                            cursor: "pointer",
                                                                            padding: "0",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                        }}
                                                                    >
                                                                        <X size={14} strokeWidth={3} />
                                                                    </button>
                                                                </span>
                                                            );
                                                        })}

                                                        {/* Search Input */}
                                                        <div style={{ flex: 1, display: "flex", alignItems: "center", minWidth: "50px" }}>
                                                            <input
                                                                type="text"
                                                                // placeholder={tempSearchParentCategory.length === 0 ? "Search..." : ""}
                                                                value={tempParentCategorySearchTerm}
                                                                onChange={(e) => setTempParentCategorySearchTerm(e.target.value)}
                                                                onFocus={() => setParentCategoryDropdownOpen(true)}
                                                                style={{
                                                                    border: "none",
                                                                    outline: "none",
                                                                    width: "100%",
                                                                    fontSize: "14px",
                                                                    background: "transparent",
                                                                    minWidth: "50px",
                                                                    color: "#212529",
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Arrow Icon - FIXED: Proper absolute positioning with click area */}
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                right: '8px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '20px',
                                                                height: '20px',
                                                                zIndex: 1, // Ensure it's above other content
                                                            }}
                                                            onClick={() => setParentCategoryDropdownOpen(!parentCategoryDropdownOpen)}
                                                        >
                                                            <AnimatedArrowIcon isOpen={parentCategoryDropdownOpen} />
                                                        </div>
                                                    </div>

                                                    {/* Parent Category Options - Only show when dropdown is open */}
                                                    {parentCategoryDropdownOpen && (
                                                        <div
                                                            className="filter-options"
                                                            style={{
                                                                marginTop: "8px",
                                                                border: "1px solid #e9ecef",
                                                                borderRadius: "8px",
                                                                maxHeight: "200px",
                                                                overflowY: "auto",
                                                                backgroundColor: "#fff",
                                                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                zIndex: 1000, // Ensure dropdown appears above other content
                                                                position: "relative", // Ensure proper stacking context
                                                            }}
                                                        >
                                                            {allCategoriesForFilter
                                                                .filter(parent => {
                                                                    if (!tempParentCategorySearchTerm) return true;
                                                                    const term = tempParentCategorySearchTerm.toLowerCase();
                                                                    return (parent.parent_name || '').toLowerCase().includes(term) ||
                                                                           (parent.parent_parent_name || '').toLowerCase().includes(term);
                                                                })
                                                                .map((parent, index, filteredArray) => {
                                                                    const isSelected = tempSearchParentCategory.includes(parent.parent_name);

                                                                    return (
                                                                        <div
                                                                            key={index}
                                                                            style={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "space-between",
                                                                                padding: "8px 12px",
                                                                                cursor: "pointer",
                                                                                fontSize: "14px",
                                                                                backgroundColor: isSelected ? "#f8f9fa" : "transparent",
                                                                            }}
                                                                            onClick={() => {
                                                                                const newSelection = isSelected
                                                                                    ? tempSearchParentCategory.filter(p => p !== parent.parent_name)
                                                                                    : [...tempSearchParentCategory, parent.parent_name];
                                                                                setTempSearchParentCategory(newSelection);
                                                                                setTempParentCategorySearchTerm("");
                                                                            }}
                                                                        >
                                                                            <div>
                                                                                <div style={{ color: '#212529', fontWeight: '400' }}>
                                                                                    {parent.parent_name}
                                                                                </div>
                                                                                <div style={{ fontSize: '12px', color: '#6C757D' }}>
                                                                                    {parent.parent_parent_name
                                                                                        ? `${parent.parent_parent_name} >> ${parent.parent_name || ''}`
                                                                                        : parent.parent_name || ''}
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                style={{
                                                                                    width: "20px",
                                                                                    height: "20px",
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                }}
                                                                            >
                                                                                {isSelected && (
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        width="20"
                                                                                        height="20"
                                                                                        viewBox="0 0 20 20"
                                                                                        fill="none"
                                                                                    >
                                                                                        <path
                                                                                            d="M16.6668 5L7.50016 14.1667L3.3335 10"
                                                                                            stroke="#6C757D"
                                                                                            strokeWidth="2"
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                        />
                                                                                    </svg>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}

                                                            {/* Show message when no results found */}
                                                            {allCategoriesForFilter
                                                                .filter(parent => {
                                                                    if (!tempParentCategorySearchTerm) return true;
                                                                    const term = tempParentCategorySearchTerm.toLowerCase();
                                                                    return (parent.parent_name || '').toLowerCase().includes(term) ||
                                                                           (parent.parent_parent_name || '').toLowerCase().includes(term);
                                                                }).length === 0 && (
                                                                    <div
                                                                        style={{
                                                                            padding: "12px",
                                                                            textAlign: "center",
                                                                            color: "#6C757D",
                                                                            fontSize: "14px",
                                                                        }}
                                                                    >
                                                                        No categories found
                                                                    </div>
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Footer */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "12px 20px",
                                                    borderTop: "1px solid #f1f1f1",
                                                }}
                                            >
                                                <button
                                                    onClick={handleClearFilter}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        color: "black",
                                                        cursor: "pointer",
                                                        fontSize: '12px',
                                                        lineHeight: 'normal',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    Clear
                                                </button>

                                                <div style={{ display: "flex", gap: "8px" }}>
                                                    <button
                                                        onClick={handleCloseFilterDrawer}
                                                        style={{
                                                            borderRadius: "8px",
                                                            padding: "10px",
                                                            cursor: "pointer",
                                                            border: "1px solid var(--navbar-dropdown-active, #212529)",
                                                            background: " var(--Background-White, #FFF)",

                                                            /* Shadow/Small */
                                                            boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
                                                            fontWeight: "500",
                                                            width: '100px',
                                                            height: '34px',
                                                            fontSize: '12px',
                                                            lineHeight: 'normal',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleApplyFilter}
                                                        style={{
                                                            background: "#000",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: "6px",
                                                            padding: "10px",
                                                            cursor: "pointer",
                                                            fontWeight: "500",
                                                            width: '100px',
                                                            height: '34px',
                                                            fontSize: '12px',
                                                            lineHeight: 'normal',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Filter
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="table-wrapper" style={{
                                        borderBottom: totalCount === 0 ? 'none' : '1px solid #dee2e6',
                                    }}>
                                        {categories.length === 0 ? (
                                            <div className="empty-state">
                                                <div className="empty-state-content">
                                                    <div class="icon"><img
                                                        src={noBrandImage}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    /></div>
                                                    <h1 class="no-item-title mt-2">
                                                        No Categories Exist
                                                    </h1>
                                                    <p class="no-item-text">
                                                        Nothing here yet. Add your first item to get started!
                                                    </p>

                                                </div>
                                            </div>
                                        ) : totalCount === 0 ? (
                                            <div className="empty-state">
                                                <div className="empty-state-content">
                                                    <div class="icon"><img
                                                        src={emptySearchImg}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    /></div>
                                                    <h1 class="no-item-title mt-2">
                                                        No Categories Found
                                                    </h1>
                                                    <p class="no-item-text">
                                                        No results match what you're looking for
                                                    </p>

                                                </div>
                                            </div>
                                        ) : (
                                            <DynamicTable
                                                columns={categoriesColumns}
                                                data={paginatedCategories}
                                                sortField={sortConfig.key}
                                                sortOrder={sortConfig.direction}
                                                handleSort={handleSort}
                                                onRowClick={onRowClick}
                                                onActionClick={handleActionClick}
                                                selectedIds={selectedIds}
                                                dropdownOpenId={dropdownOpenId}
                                                suppressHighlight={suppressHighlight}
                                                buttonRefs={buttonRefs}
                                                rowKey="category_id"
                                                searchTerm={activeSearchTerm}
                                                selectedStatuses={searchStatus}
                                                selectedParentBrands={searchParentCategory}
                                                emptySearchImg={emptySearchImg}
                                                noBrandImage={noBrandImage}
                                                emptyImage={noBrandImage}
                                                emptyTitle="No Categories Found"
                                                emptyMessage="No results match what you're looking for"
                                                showActions={true}
                                            />
                                        )}

                                        {dropdownOpenId !== null && currentRow && (
                                            <div
                                                ref={dropdownRef}
                                                className="backdrop-cls"
                                                style={{
                                                    position: "absolute",
                                                    top: dropdownPosition.top,
                                                    left: dropdownPosition.left,
                                                    background: "white",
                                                    boxShadow: "0px 1px 4px 0px #0000001A",
                                                    zIndex: 1000,
                                                    minWidth: 152,
                                                    borderRadius: 8,
                                                }}
                                                onClick={e => e.stopPropagation()}
                                            >
                                                <div className="btn-align-forall">
                                                    <div className="common-for-all">
                                                        <button onClick={
                                                            () => {
                                                                const row = paginatedCategories.find(d => d.category_id === dropdownOpenId);
                                                                onRowClick(row);
                                                                setDropdownOpenId(null);
                                                            }
                                                        }>
                                                            View Details
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        {/* FIXED: Pass the category ID directly to editCategory */}
                                                        <button
                                                            onClick={editCategory}
                                                        >
                                                            Edit
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button
                                                            onClick={() => {
                                                                const actionType = currentRow.status_text === 'Active' ? 'deactivate' : 'activate';
                                                                openDeactivateModal(currentRow.category_id, actionType);
                                                                setDropdownOpenId(null);
                                                            }}
                                                        >
                                                            {currentRow.status_text === 'Active' ? 'Deactivate' : 'Activate'}
                                                        </button>
                                                    </div>
                                                    <div className="common-for-all">
                                                        <button
                                                            onClick={() => {
                                                                openArchiveModal(currentRow.category_id, currentRow.category_name);
                                                                setDropdownOpenId(null);
                                                            }}
                                                            className="deactivate-cls"
                                                        >
                                                            Archive
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {totalCount > 0 && (
                                        <div className="pagination">
                                            <div className="pagination-info">
                                                {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(currentPage * PAGE_SIZE, totalCount)} of ${totalCount}`}
                                            </div>
                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={handleCategoriesPageChange}
                                            />
                                        </div>
                                    )}
                                    {saveModalOpen && (
                                        <>
                                            <div className="drawer-overlay" />
                                            <div
                                                className="entity-drawer modal-popup-cls"
                                                ref={saveRef}
                                                style={{
                                                    maxWidth: 650,
                                                    margin: "0 auto",
                                                    background: "white",
                                                    borderRadius: 12,
                                                    zIndex: 1050,
                                                    padding: 24,
                                                    position: "fixed",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                                    height: "auto",
                                                    right: "auto",
                                                    overflow: "inherit",
                                                }}
                                            >
                                                <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                                    <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                                <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                                                <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                                                <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                                    <button className="btn-save" onClick={handleSaveConfirm}>
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {drawerOpen && selectedRow && (
                            <>
                                <div className="drawer-overlay" />
                                <div className="entity-drawer" ref={drawerRef}>
                                    <div className="drawer-header aa" style={{ backgroundColor: "var(--Gray-100, #F8F9FA)" }}>
                                        <div className="d-flex align-items-center gap-1">
                                            {(navigationStack.length > 1 || location.search.includes('from=spusku')) && (
                                                <button
                                                    className="back-btn"
                                                    onClick={() => {
                                                        if (navigationStack.length > 1) {
                                                            handleDrawerBack();
                                                        } else if (location.search.includes('from=spusku')) {
                                                            const query = new URLSearchParams(location.search);
                                                            const spuId = query.get('spuId');
                                                            const spuTab = query.get('spuTab');

                                                            history.push(`/spu-sku?id=${spuId}&autoOpen=true&tab=${spuTab}`);
                                                            setDrawerOpen(false);
                                                            setSelectedRow(null);
                                                            setNavigationStack([]);
                                                            setHasAutoOpened(false);
                                                        }
                                                    }}
                                                    style={{
                                                        backgroundColor: 'transparent',
                                                        border: 'none',
                                                        padding: 0,
                                                        margin: 0,
                                                    }}
                                                >
                                                    <ChevronLeft size={24} />
                                                </button>
                                            )}
                                            <h3>Category Info</h3>
                                        </div>
                                        <button
                                            className="close-btn"
                                            onClick={() => {
                                                setDrawerOpen(false);
                                                setSelectedIds(ids => ids.filter(id => id !== selectedRow?.external_id));
                                                setSelectedRow(null);
                                                setNavigationStack([]);
                                            }}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="main-drawer-inner">
                                        <div className="drawer-title d-flex align-items-center justify-content-between" style={{ borderBottom: 'none' }}>
                                            <div className='d-flex align-items-center' style={{ maxWidth: '320px' }}>
                                                <h2 className='pb-0' style={{ textTransform: 'none' }}>{selectedRow?.category_name}</h2>
                                            </div>
                                            <div className='entities-tab'>
                                                <div className='toolbar'>
                                                    <div className='toolbar-right' style={{ position: "relative" }} ref={toolbarRef}>
                                                        {/* FIXED: Pass the selected row's category ID directly */}
                                                        <button
                                                            className="dark-border gray-btn mx-2"
                                                            type='button'
                                                            onClick={editCategory}
                                                            style={{ width: "100px" }}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            className="drawer-dots"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleToolbarDropdown();
                                                            }}
                                                        >
                                                            <MoreHorizontal size={18} />
                                                        </button>

                                                        {toolbarDropdownOpen && (
                                                            <div
                                                                className="backdrop-cls"
                                                                style={{
                                                                    position: "absolute",
                                                                    top: 40,
                                                                    right: 0,
                                                                    background: "white",
                                                                    boxShadow: "0px 1px 4px 0px #0000001A",
                                                                    zIndex: 1000,
                                                                    minWidth: 152,
                                                                    borderRadius: 8,
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                            >
                                                                <div className="btn-align-forall">

                                                                    <div className="common-for-all">
                                                                        <button onClick={() => {
                                                                            const actionType = selectedRow.status_text === 'Active' ? 'deactivate' : 'activate';
                                                                            openDeactivateModal(selectedRow.category_id, actionType);
                                                                            setToolbarDropdownOpen(false);
                                                                        }}>
                                                                            {selectedRow.status_text === 'Active' ? 'Deactivate' : 'Activate'}
                                                                        </button>
                                                                    </div>
                                                                    <div className="common-for-all">
                                                                        <button onClick={() => { openArchiveModal(selectedRow.category_id, selectedRow.category_name); setToolbarDropdownOpen(false); }} className="deactivate-cls">
                                                                            Archive
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="drawer-tabs" style={{ marginBottom: '20px' }}>
                                            <button
                                                className={drawerTab === 'general-info' ? 'active' : ''}
                                                onClick={() => setDrawerTab('general-info')}
                                            >
                                                General
                                            </button>
                                            <button
                                                className={drawerTab === 'additional-info' ? 'active' : ''}
                                                onClick={() => setDrawerTab('additional-info')}
                                            >
                                                Additional
                                            </button>
                                        </div>
                                        <div className="drawer-content">
                                            {drawerTab === 'general-info' && (
                                                <div className="tab-panel general-info-panel">
                                                    <div className='' style={{ background: 'var(--Background-White, #FFF)', marginBottom: '10px', boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px' }}>
                                                        <div className='px-15 py-20' style={{ borderBottom: '.0625rem solid rgba(46, 54, 80, .125)', color: 'var(--Gray-900, #212529)', padding: '20px 15px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: 'normal', background: '#F8F9FA', borderRadius: ' 12px 12px 0 0' }}>Basic Info</div>
                                                        <div className='bg-white' style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                                            gap: '20px',
                                                            border: '1px solid #ffffffff',
                                                            boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px',
                                                            borderRadius: '0 0 12px 12px',
                                                            padding: '20px',
                                                            alignItems: 'start'
                                                        }}>

                                                            {/* Category Name Field - Dynamic layout based on content length */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                // minHeight: '60px',
                                                                gridColumn: isCategoryNameOverflowing(selectedRow.category_name) ? '1 / -1' : '1',
                                                                gridRow: '1',
                                                                padding: '0px'
                                                            }}>
                                                                <div className="field-label">Category Name</div>
                                                                <div className="field-value" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    whiteSpace: 'normal'
                                                                }}>
                                                                    {selectedRow.category_name}
                                                                </div>
                                                            </div>

                                                            {/* Status Field - Position depends on Category Name length */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                // minHeight: '60px',
                                                                gridColumn: isCategoryNameOverflowing(selectedRow.category_name) ? '1' : '2',
                                                                gridRow: isCategoryNameOverflowing(selectedRow.category_name) ? '2' : '1',
                                                                padding: '0px'
                                                            }}>
                                                                <div className="field-label">Status</div>
                                                                <div className="field-value">
                                                                    <span className={`status ${typeof selectedRow?.status_text === 'string' ? selectedRow.status_text?.toLowerCase() : ''}`}>
                                                                        {selectedRow.status_text || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* ID Field - Always full width with text wrapping */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                padding: '0px',
                                                                gridColumn: '1 / -1',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                width: '100%',
                                                                // minHeight: '60px'
                                                            }}>
                                                                <div className="field-label">ID</div>
                                                                <div className="field-value" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    whiteSpace: 'normal',
                                                                    lineHeight: '1.4'
                                                                }}>
                                                                    {selectedRow?.external_id || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                </div>
                                                            </div>

                                                            {/* Open ID Field - Always full width with text wrapping */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                padding: '0px',
                                                                gridColumn: '1 / -1',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                width: '100%',
                                                                // minHeight: '60px'
                                                            }}>
                                                                <div className="field-label">Open ID</div>
                                                                <div className="field-value d-flex align-items-center gap-2" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    whiteSpace: 'normal',
                                                                    flexWrap: 'wrap',
                                                                    lineHeight: 'normal',
                                                                    fontWeight: 600,
                                                                    fontSize: '12px'
                                                                }}>
                                                                    <div style={{ position: "relative", display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                        <span
                                                                            ref={openIdRef}
                                                                            onClick={handleCopy}
                                                                            style={{
                                                                                color: copied ? "#60aaffff" : "#2C7BE5",
                                                                                cursor: "pointer",
                                                                                textDecoration: "none",
                                                                                transition: "color 0.3s",
                                                                                marginRight: "4px",
                                                                                wordBreak: 'break-all'
                                                                            }}
                                                                        >
                                                                            {selectedRow.uuid || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </span>
                                                                        <Copy
                                                                            size={13}
                                                                            onClick={handleCopy}
                                                                            style={{ cursor: "pointer", color: copied ? "#60aaffff" : "#2C7BE5", flexShrink: 0 }}
                                                                        />
                                                                        {copied && <span style={{ fontSize: '12px', color: "#2C7BE5", flexShrink: 0 }}>Copied!</span>}

                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Remaining fields in standard 2-column layout */}
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Created By</div>
                                                                <div className="field-value">{selectedRow.created_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Created Time</div>
                                                                <div className="field-value">{formatDateTime(selectedRow.created_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label" style={{ borderBottom: 'none' }}>Last Modified By</div>
                                                                <div className="field-value">{selectedRow.modified_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Last Modified Time</div>
                                                                <div className="field-value">{formatDateTime(selectedRow.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='bg-white' style={{ border: '1px solid #ffffff', borderRadius: '12px', background: 'var(--Background-White, #FFF)', boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px' }}>
                                                        <div className='px-15 py-20' style={{ borderBottom: '.0625rem solid rgba(46, 54, 80, .125)', color: 'var(--Gray-900, #212529)', padding: '20px 15px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: 'normal', background: '#F8F9FA', borderRadius: ' 12px 12px 0 0' }}>Business Info</div>
                                                        <div className='bg-white' style={{ display: 'grid', borderRadius: '0 0 12px 12px', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', boxShadow: '0px 0px 1px #fbfbfbff', padding: '20px' }}>
                                                            <div className="field-block"
                                                                style={{
                                                                    borderBottom: 'none',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    padding: '0px',
                                                                    gridColumn: isCategoryNameOverflowing(selectedRow.parent_name) ? '1 / -1' : '1',
                                                                    gridRow: '1',
                                                                }}
                                                            >
                                                                <div className="field-label">Parent Category</div>
                                                                <div className="field-value">
                                                                    <div className='d-flex align-items-center'>
                                                                        {selectedRow.parent_name !== null ? (

                                                                            <div
                                                                                onClick={() => handleParentCategoryClick(selectedRow.parent_name)}
                                                                                // className="d-flex align-items-center gap-1 me-3"
                                                                                style={{
                                                                                    color: '#2C7BE5',
                                                                                    textDecoration: 'underline',
                                                                                    fontWeight: '600',
                                                                                    fontSize: '12px',
                                                                                    background: 'none',
                                                                                    border: 'none',
                                                                                    padding: 0,
                                                                                    cursor: 'pointer',
                                                                                }}
                                                                            >
                                                                                {selectedRow.parent_name}
                                                                                {/* <div> */}
                                                                                <SquareArrowOutUpRight size={14} strokeWidth={3} style={{ marginLeft: '4px' }} />
                                                                                {/* </div> */}
                                                                            </div>

                                                                        ) : (
                                                                            <span>{selectedRow.parent_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                padding: '0px',
                                                                gridColumn: isCategoryNameOverflowing(selectedRow.parent_name) ? '1' : '2',
                                                                gridRow: isCategoryNameOverflowing(selectedRow.parent_name) ? '2' : '1',
                                                            }}>
                                                                <div className="field-label">Prefix</div>
                                                                <div className="field-value">{selectedRow.prefix || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='main-company-body-inner'>
                                                        <div className='edit-organisation-info d-flex justify-content-end align-items-center gap-2'>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {drawerTab === 'additional-info' && (
                                                <div className="tab-panel additional-info-panel" style={{ borderRadius: '12px', marginTop: '20px', boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px' }}>
                                                    <p className="note-type-added cat-notes" style={{ fontSize: '14px', background: 'var(--Gray-100, #F8F9FA)', borderRadius: '12px 12px 0 0', borderBottom: '0.0625rem solid rgba(46, 54, 80, 0.125)' }}>Notes</p>
                                                    <label className="no-description-cls notes-preserve" style={{ padding: "20px 20px", background: "var(--Background-White, #FFF)", borderRadius: '0 0 12px 12px' }}>{selectedRow.notes || <span style={{ color: '#ADB5BD' }}>NA</span>}</label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Deactivate Confirmation Modal */}
                        <ConfirmDeactivateModal
                            isOpen={deactivateModalOpen}
                            onCancel={() => {
                                setDeactivateModalOpen(false);
                                setCategoryToDeactivate(null);
                            }}
                            onConfirm={handleDeactivateConfirm}
                            entityName="category"
                            actionType={deactivateActionType}
                        />

                        {/* Archive Confirmation Modal */}
                        <ConfirmArchiveModal
                            isOpen={archiveModalOpen}
                            onCancel={() => setArchiveModalOpen(false)}
                            onConfirm={() => handleArchiveConfirm(categoryToArchive)}
                            entityName="Category"
                        />

                        {/* SPU Validation Error Modal */}
                        {spuValidationModalOpen && (
                            <>
                                <div className="drawer-overlay" />
                                <div className="entity-drawer modal-popup-cls"
                                    style={{
                                        maxWidth: 500,
                                        margin: "0 auto",
                                        background: "white",
                                        borderRadius: 12,
                                        zIndex: 1050,
                                        padding: 24,
                                        position: "fixed",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",
                                        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                        height: "auto",
                                        right: "auto",
                                        overflow: "inherit",
                                    }}>
                                    <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                        <button className="close-btn" onClick={() => setSpuValidationModalOpen(false)}>
                                            <X size={20} />
                                        </button>
                                    </div>
                                    <h3 style={{
                                        textAlign: "left",
                                        color: "#212529",
                                        fontSize: "20px",
                                        fontWeight: "600",
                                        lineHeight: "24px",
                                        fontFamily: "Montserrat",
                                        marginBottom: "16px"
                                    }}>
                                        The category can't be archive
                                    </h3>
                                    <p style={{
                                        marginBottom: "24px",
                                        textAlign: "left",
                                        color: "#6C757D",
                                        fontSize: "14px",
                                        fontWeight: "400",
                                        lineHeight: "20px",
                                        fontFamily: "Montserrat"
                                    }}>
                                        {archiveMessage}
                                    </p>
                                    <div className="drawer-footer" style={{
                                        display: "flex",
                                        justifyContent: "flex-end",
                                        paddingTop: "10px"
                                    }}>
                                        <button
                                            className="btn-save"
                                            onClick={() => {
                                                setSpuValidationModalOpen(false);
                                                setArchiveMessage('');
                                            }}
                                            style={{
                                                background: "#000",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: "6px",
                                                padding: "8px 16px",
                                                cursor: "pointer",
                                                fontWeight: "500",
                                            }}
                                        >
                                            OK
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Archived Categories Tab - FIXED SEARCH FUNCTIONALITY */}
                {activeTab === 'archived-categories' && (
                    <div className="tab-content" id="archived-categories-content">
                        <div className="info-section">
                            <div className="info-card">
                                <div className="entities-tab">
                                    <div className="toolbar">
                                        <div className="toolbar-left">
                                            <div className="search-box">
                                                <Search size={20} />
                                                <input
                                                    type="text"
                                                    placeholder="Search…"
                                                    value={archivedSearchTerm}
                                                    onChange={handleArchivedCategoriesSearchChange}
                                                    onKeyDown={handleArchivedCategoriesSearch}
                                                />
                                                {archivedSearchTerm && (
                                                    <X
                                                        size={12}
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: '#000000ff',
                                                        }}
                                                        onClick={handleClearArchivedSearch}
                                                    />
                                                )}
                                            </div>
                                            <button
                                                ref={archivedFilterButtonRef}
                                                className={`icon-btn p-2 filter-btn cat-filter-btn ${archivedFiltersApplied ? 'applied' : ''}`}
                                                onClick={handleArchivedFilterButtonClick}
                                                style={{
                                                    border: archivedFiltersApplied ? '1px solid #2C7BE5' : '1px solid #6C757D',
                                                    color: archivedFiltersApplied ? '#2C7BE5' : '#141B34',
                                                    height: '40px',
                                                    background: archivedFiltersApplied ? '#CFE2FF' : '#ffffffff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                {archivedFiltersApplied ? (
                                                    <FilterActiveIcon />
                                                ) : (
                                                    <FilterInactiveIcon />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    {archivedFilterPopupOpen && (
                                        <div
                                            ref={archivedFilterPopupRef}
                                            style={{
                                                position: "fixed",
                                                top: 0,
                                                right: 0,
                                                width: 360,
                                                height: "100vh",
                                                backgroundColor: "#fff",
                                                boxShadow: "-3px 0 12px rgba(0,0,0,0.1)",
                                                zIndex: 2000,
                                                display: "flex",
                                                flexDirection: "column",
                                                animation: "slideIn 0.3s ease-out",
                                            }}
                                        >
                                            {/* Header */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "16px 20px",
                                                    borderBottom: "1px solid #f1f1f1",
                                                }}
                                            >
                                                <h4 style={{ fontSize: '14px', margin: 0, fontWeight: 600, color: "#212529", fontFamily: 'Montserrat' }}>Filter</h4>
                                                <button
                                                    onClick={handleCloseFilterDrawer}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        cursor: "pointer",
                                                        padding: "4px",
                                                    }}
                                                >
                                                    <X size={18} />
                                                </button>
                                            </div>

                                            {/* Body */}
                                            <div
                                                style={{
                                                    flex: 1,
                                                    overflowY: "auto",
                                                    padding: "20px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: "24px",
                                                }}
                                            >
                                                {/* Parent Category Filter - FIXED with up/down arrows and click outside handling */}
                                                <div ref={archivedParentCategoryDropdownRef}>
                                                    <label
                                                        style={{
                                                            display: "block",
                                                            fontWeight: 600,
                                                            marginBottom: "8px",
                                                            color: "#212529",
                                                            fontFamily: 'Montserrat',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        Parent Category
                                                    </label>

                                                    {/* Search Input with Chips and Arrow */}
                                                    <div
                                                        className="search-box-filter"
                                                        style={{
                                                            position: "relative",
                                                            border: "1px solid #d9d9d9",
                                                            borderRadius: "8px",
                                                            padding: "4px 32px 4px 8px", // Increased right padding for arrow space
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            alignItems: "center",
                                                            gap: "6px",
                                                            minHeight: "36px",
                                                        }}
                                                    >
                                                        {/* Selected Categories Chips */}
                                                        {tempArchivedSearchParentCategory.map((parent) => {
                                                            if (!parent) return null;
                                                            return (
                                                                <span
                                                                    key={parent}
                                                                    style={{
                                                                        backgroundColor: "#ffeb00",
                                                                        color: "#212529",
                                                                        borderRadius: "16px",
                                                                        padding: "4px 8px",
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        fontSize: "12px",
                                                                        fontWeight: "600",
                                                                        // whiteSpace: "nowrap",
                                                                        overflow: 'hidden'
                                                                    }}
                                                                >
                                                                    {parent}
                                                                    <button
                                                                        onClick={() => {
                                                                            setTempArchivedSearchParentCategory(prev => prev.filter(p => p !== parent));
                                                                            setTempArchivedParentCategorySearchTerm("");
                                                                        }}
                                                                        style={{
                                                                            background: "none",
                                                                            border: "none",
                                                                            marginLeft: "6px",
                                                                            cursor: "pointer",
                                                                            padding: "0",
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                        }}
                                                                    >
                                                                        <X size={14} strokeWidth={3} />
                                                                    </button>
                                                                </span>
                                                            );
                                                        })}

                                                        {/* Search Input */}
                                                        <div style={{ flex: 1, display: "flex", alignItems: "center", minWidth: "50px" }}>
                                                            <input
                                                                type="text"
                                                                // placeholder={tempArchivedSearchParentCategory.length === 0 ? "Search..." : ""}
                                                                value={tempArchivedParentCategorySearchTerm}
                                                                onChange={(e) => setTempArchivedParentCategorySearchTerm(e.target.value)}
                                                                onFocus={() => setArchivedParentCategoryDropdownOpen(true)}
                                                                style={{
                                                                    border: "none",
                                                                    outline: "none",
                                                                    width: "100%",
                                                                    fontSize: "14px",
                                                                    background: "transparent",
                                                                    minWidth: "50px",
                                                                    color: "#212529",
                                                                }}
                                                            />
                                                        </div>

                                                        {/* Arrow Icon - FIXED: Centered with absolute positioning */}
                                                        <div
                                                            style={{
                                                                position: 'absolute',
                                                                right: '8px',
                                                                top: '50%',
                                                                transform: 'translateY(-50%)',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                width: '20px',
                                                                height: '20px',
                                                                zIndex: 1,
                                                            }}
                                                            onClick={() => setArchivedParentCategoryDropdownOpen(!archivedParentCategoryDropdownOpen)}
                                                        >
                                                            <AnimatedArrowIcon isOpen={archivedParentCategoryDropdownOpen} />
                                                        </div>
                                                    </div>

                                                    {/* Parent Category Options - Only show when dropdown is open */}
                                                    {archivedParentCategoryDropdownOpen && (
                                                        <div
                                                            className="filter-options"
                                                            style={{
                                                                marginTop: "8px",
                                                                border: "1px solid #e9ecef",
                                                                borderRadius: "8px",
                                                                maxHeight: "200px",
                                                                overflowY: "auto",
                                                                backgroundColor: "#fff",
                                                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                                                                zIndex: 1000, // Ensure dropdown appears above other content
                                                                position: "relative", // Ensure proper stacking context
                                                            }}
                                                        >
                                                            {allCategoriesForFilter
                                                                .filter(parent => {
                                                                    if (!tempArchivedParentCategorySearchTerm) return true;
                                                                    const term = tempArchivedParentCategorySearchTerm.toLowerCase();
                                                                    return (parent.parent_name || '').toLowerCase().includes(term) ||
                                                                           (parent.parent_parent_name || '').toLowerCase().includes(term);
                                                                })
                                                                .map((parent, index, filteredArray) => {
                                                                    const isSelected = tempArchivedSearchParentCategory.includes(parent.parent_name);

                                                                    return (
                                                                        <div
                                                                            key={index}
                                                                            style={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                justifyContent: "space-between",
                                                                                padding: "8px 12px",
                                                                                cursor: "pointer",
                                                                                fontSize: "14px",
                                                                                backgroundColor: isSelected ? "#f8f9fa" : "transparent",
                                                                            }}
                                                                            onClick={() => {
                                                                                const newSelection = isSelected
                                                                                    ? tempArchivedSearchParentCategory.filter(p => p !== parent.parent_name)
                                                                                    : [...tempArchivedSearchParentCategory, parent.parent_name];
                                                                                setTempArchivedSearchParentCategory(newSelection);
                                                                                setTempArchivedParentCategorySearchTerm("");
                                                                            }}
                                                                        >
                                                                            <div>
                                                                                <div style={{ color: '#212529', fontWeight: '400' }}>
                                                                                    {parent.parent_name}
                                                                                </div>
                                                                                <div style={{ fontSize: "12px", color: '#6C757D' }}>
                                                                                    {parent.parent_parent_name
                                                                                        ? `${parent.parent_parent_name} >> ${parent.parent_name || ""}`
                                                                                        : parent.parent_name || ""}
                                                                                </div>
                                                                            </div>
                                                                            <div
                                                                                style={{
                                                                                    width: "20px",
                                                                                    height: "20px",
                                                                                    display: "flex",
                                                                                    alignItems: "center",
                                                                                    justifyContent: "center",
                                                                                }}
                                                                            >
                                                                                {isSelected && (
                                                                                    <svg
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                        width="20"
                                                                                        height="20"
                                                                                        viewBox="0 0 20 20"
                                                                                        fill="none"
                                                                                    >
                                                                                        <path
                                                                                            d="M16.6668 5L7.50016 14.1667L3.3335 10"
                                                                                            stroke="#6C757D"
                                                                                            strokeWidth="2"
                                                                                            strokeLinecap="round"
                                                                                            strokeLinejoin="round"
                                                                                        />
                                                                                    </svg>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}

                                                            {/* Show message when no results found */}
                                                            {allCategoriesForFilter
                                                                .filter(parent => {
                                                                    if (!tempArchivedParentCategorySearchTerm) return true;
                                                                    const term = tempArchivedParentCategorySearchTerm.toLowerCase();
                                                                    return (parent.parent_name || '').toLowerCase().includes(term) ||
                                                                           (parent.parent_parent_name || '').toLowerCase().includes(term);
                                                                }).length === 0 && (
                                                                    <div
                                                                        style={{
                                                                            padding: "12px",
                                                                            textAlign: "center",
                                                                            color: "#6C757D",
                                                                            fontSize: "14px",
                                                                        }}
                                                                    >
                                                                        No categories found
                                                                    </div>
                                                                )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Footer */}
                                            <div
                                                style={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    padding: "12px 20px",
                                                    borderTop: "1px solid #f1f1f1",
                                                }}
                                            >
                                                <button
                                                    onClick={handleArchivedClearFilter}
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        color: "black",
                                                        cursor: "pointer",

                                                        fontSize: '12px',
                                                        lineHeight: 'normal',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    Clear
                                                </button>

                                                <div style={{ display: "flex", gap: "8px" }}>
                                                    <button
                                                        onClick={handleCloseFilterDrawer}
                                                        style={{
                                                            borderRadius: "8px",
                                                            padding: "10px",
                                                            cursor: "pointer",
                                                            border: "1px solid var(--navbar-dropdown-active, #212529)",
                                                            background: " var(--Background-White, #FFF)",

                                                            /* Shadow/Small */
                                                            boxShadow: "0 1px 4px 0 rgba(0, 0, 0, 0.10)",
                                                            fontWeight: "500",
                                                            width: '100px',
                                                            height: '34px',
                                                            fontSize: '12px',
                                                            lineHeight: 'normal',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={handleArchivedApplyFilter}
                                                        style={{
                                                            background: "#000",
                                                            color: "#fff",
                                                            border: "none",
                                                            borderRadius: "6px",
                                                            padding: "10",
                                                            cursor: "pointer",
                                                            fontWeight: "500",
                                                            width: '100px',
                                                            height: '34px',
                                                            fontSize: '12px',
                                                            lineHeight: 'normal',
                                                            fontWeight: 600
                                                        }}
                                                    >
                                                        Filter
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}


                                    <div className="table-wrapper" style={{
                                        borderBottom: archivedCategories.length === 0 || archivedFiltered.length === 0 ? 'none' : '1px solid #dee2e6',
                                    }}>
                                        {archivedCategories.length === 0 ? (
                                            <div className="empty-state">
                                                <div className="empty-state-content">
                                                    <div class="icon"><img
                                                        src={noBrandImage}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    /></div>
                                                    <h1 class="no-item-title mt-2">
                                                        No Archived Categories Exist
                                                    </h1>
                                                    <p class="no-item-text">
                                                        Nothing here yet. Add your first item to get started!
                                                    </p>

                                                </div>
                                            </div>
                                        ) : archivedFiltered.length === 0 ? (
                                            <div className="empty-state">
                                                <div className="empty-state-content">
                                                    <div class="icon"><img
                                                        src={emptySearchImg}
                                                        alt="avatar"
                                                        className="avatar"
                                                        style={{
                                                            width: 120,
                                                            height: 120,
                                                            verticalAlign: 'middle'
                                                        }}
                                                    /></div>
                                                    <h1 class="no-item-title mt-2">
                                                        No Archived Categories Found
                                                    </h1>
                                                    <p class="no-item-text">
                                                        No results match what you're looking for
                                                    </p>

                                                </div>
                                            </div>
                                        ) : (
                                            <DynamicTable
                                                columns={archivedColumns}
                                                data={paginatedArchivedCategories}
                                                sortField={archivedSortConfig.key}
                                                sortOrder={archivedSortConfig.direction}
                                                handleSort={handleArchivedSort}
                                                onRowClick={onRowClick}
                                                selectedIds={archivedSelectedIds}
                                                dropdownOpenId={null}
                                                suppressHighlight={suppressHighlight}
                                                buttonRefs={buttonRefs}
                                                rowKey="category_id"
                                                searchTerm={archivedActiveSearchTerm}
                                                selectedStatuses={archivedSearchStatus}
                                                selectedParentBrands={archivedSearchParentCategory}
                                                emptySearchImg={emptySearchImg}
                                                noBrandImage={noBrandImage}
                                                emptyImage={noBrandImage}
                                                emptyTitle="No Archived Categories Found"
                                                emptyMessage="No results match what you're looking for"
                                                showActions={false}
                                            />
                                        )}
                                    </div>
                                    {archivedTotalCount > 0 && (
                                        <div className="pagination">
                                            <div className="pagination-info">
                                                {` ${(archivedCurrentPage - 1) * PAGE_SIZE + 1} - ${Math.min(archivedCurrentPage * PAGE_SIZE, archivedTotalCount)} of ${archivedTotalCount} `}
                                            </div>
                                            <Pagination
                                                currentPage={archivedCurrentPage}
                                                totalPages={archivedTotalPages}
                                                onPageChange={handleArchivedCategoriesPageChange}
                                            />
                                        </div>
                                    )}
                                    {saveModalOpen && (
                                        <>
                                            <div className="drawer-overlay" />
                                            <div
                                                className="entity-drawer modal-popup-cls"
                                                ref={saveRef}
                                                style={{
                                                    maxWidth: 650,
                                                    margin: "0 auto",
                                                    background: "white",
                                                    borderRadius: 12,
                                                    zIndex: 1050,
                                                    padding: 24,
                                                    position: "fixed",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)",
                                                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                                                    height: "auto",
                                                    right: "auto",
                                                    overflow: "inherit",
                                                }}
                                            >
                                                <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                                    <button className="close-btn" onClick={() => setSaveModalOpen(false)}>
                                                        <X size={20} />
                                                    </button>
                                                </div>
                                                <h3 style={{ textAlign: "left", color: "#212529", fontSize: "20px", fontWeight: "600", lineHeight: "24px", fontFamily: "Montserrat", marginBottom: "5px" }}>New feature in Progress</h3>
                                                <p style={{ marginBottom: "0", textAlign: "left", color: "#6C757D", fontSize: "14px", fontWeight: "400", lineHeight: "20px", fontFamily: "Montserrat" }}>Work in Progress</p>
                                                <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                                                    <button className="btn-save" onClick={handleSaveConfirm}>
                                                        OK
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        {drawerOpen && selectedRow && (
                            <>
                                <div className="drawer-overlay" />
                                <div className="entity-drawer" ref={drawerRef}>
                                    <div className="drawer-header" style={{ backgroundColor: "var(--Gray-100, #F8F9FA)" }}>
                                        {location.search.includes('from=spusku') && (
                                            <button
                                                className="back-btn"
                                                onClick={() => {
                                                    const query = new URLSearchParams(location.search);
                                                    const spuId = query.get('spuId');
                                                    const spuTab = query.get('spuTab');
                                                    history.push(`/spu-sku?id=${spuId}&autoOpen=true&tab=${spuTab}`);
                                                    setDrawerOpen(false);
                                                    setSelectedRow(null);
                                                    setHasAutoOpened(false); // Reset to allow re-opening
                                                }}
                                                style={{ backgroundColor: 'transparent', border: 'none', padding: 0, margin: 0 }}
                                            >
                                                <ChevronLeft size={24} />
                                            </button>
                                        )}
                                        <h3> Category Info</h3>
                                        <button
                                            className="close-btn"
                                            onClick={() => {
                                                setDrawerOpen(false);
                                                setSelectedIds(ids => ids.filter(id => id !== selectedRow?.external_id));
                                                setSelectedRow(null);
                                            }}
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                    <div className="main-drawer-inner">
                                        <div className="drawer-title d-flex align-items-center justify-content-between" style={{ borderBottom: 'none' }}>
                                            <div className='d-flex align-items-center'>
                                                <h2 className='pb-0' style={{ textTransform: 'none' }}>{selectedRow?.category_name}</h2>
                                            </div>
                                            {/* Remove action buttons for archived categories */}
                                        </div>
                                        <div className="drawer-tabs" style={{ marginBottom: '20px' }}>
                                            <button
                                                className={drawerTab === 'general-info' ? 'active' : ''}
                                                onClick={() => setDrawerTab('general-info')}
                                            >
                                                General
                                            </button>
                                            <button
                                                className={drawerTab === 'additional-info' ? 'active' : ''}
                                                onClick={() => setDrawerTab('additional-info')}
                                            >
                                                Additional
                                            </button>
                                        </div>
                                        <div className="drawer-content">
                                            {drawerTab === 'general-info' && selectedRow.is_archived && (
                                                <div className="tab-panel general-info-panel" >
                                                    <div className='' style={{ background: 'var(--Background-White, #FFF)', marginBottom: '10px', boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px' }}>
                                                        <div className='px-15 py-20' style={{ borderBottom: '.0625rem solid rgba(46, 54, 80, .125)', color: 'var(--Gray-900, #212529)', padding: '20px 15px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: 'normal', background: '#F8F9FA', borderRadius: ' 12px 12px 0 0' }}>Basic Info</div>
                                                        <div className='bg-white' style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                                            gap: '20px',
                                                            border: '1px solid #ffffffff',
                                                            boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px',
                                                            borderRadius: '0 0 12px 12px',
                                                            padding: '20px',
                                                            alignItems: 'start'
                                                        }}>
                                                            {/* Category Name Field - Dynamic layout based on content length */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                // minHeight: '60px',
                                                                gridColumn: isCategoryNameOverflowing(selectedRow.category_name) ? '1 / -1' : '1',
                                                                gridRow: '1',
                                                                padding: '0px'
                                                            }}>
                                                                <div className="field-label">Category Name</div>
                                                                <div className="field-value" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    whiteSpace: 'normal'
                                                                }}>
                                                                    {selectedRow.category_name}
                                                                </div>
                                                            </div>

                                                            {/* Status Field - Position depends on Category Name length */}
                                                            {/* <div className="field-block" style={{
                                                                borderBottom:'none', 
                                                                display: 'flex', 
                                                                flexDirection: 'column',
                                                                minHeight: '60px',
                                                                gridColumn: isCategoryNameOverflowing(selectedRow.category_name) ? '1' : '2',
                                                                gridRow: isCategoryNameOverflowing(selectedRow.category_name) ? '2' : '1'
                                                            }}>
                                                                <div className="field-label">Status</div>
                                                                <div className="field-value">
                                                                    <span style={{ color: '#6C757D', fontStyle: 'italic' }}>
                                                                        Archived
                                                                    </span>
                                                                </div>
                                                            </div> */}

                                                            {/* ID Field - Always full width with text wrapping */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                // padding: '9px 0',
                                                                gridColumn: '1 / -1',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                width: '100%',
                                                                padding: '0px',
                                                                padding: '0px'


                                                            }}>
                                                                <div className="field-label">ID</div>
                                                                <div className="field-value" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    whiteSpace: 'normal',
                                                                    lineHeight: '1.4'
                                                                }}>
                                                                    {selectedRow?.external_id || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                </div>
                                                            </div>

                                                            {/* Open ID Field - Always full width with text wrapping */}
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none',
                                                                padding: '9px 0',
                                                                gridColumn: '1 / -1',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start',
                                                                width: '100%',
                                                                // minHeight: '60px',
                                                                padding: '0px'

                                                            }}>
                                                                <div className="field-label">Open ID</div>
                                                                <div className="field-value d-flex align-items-center gap-2" style={{
                                                                    wordWrap: 'break-word',
                                                                    overflowWrap: 'break-word',
                                                                    whiteSpace: 'normal',
                                                                    flexWrap: 'wrap',
                                                                    lineHeight: 'normal',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600
                                                                }}>
                                                                    <div style={{ position: "relative", display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                        <span
                                                                            ref={openIdRef}
                                                                            onClick={handleCopy}
                                                                            style={{
                                                                                color: copied ? "#60aaffff" : "#2C7BE5",
                                                                                cursor: "pointer",
                                                                                textDecoration: "none",
                                                                                transition: "color 0.3s",
                                                                                marginRight: "4px",
                                                                                wordBreak: 'break-all'
                                                                            }}
                                                                        >
                                                                            {selectedRow.uuid || <span style={{ color: '#ADB5BD' }}>NA</span>}
                                                                        </span>
                                                                        <Copy
                                                                            size={13}
                                                                            onClick={handleCopy}
                                                                            style={{ cursor: "pointer", color: copied ? "#60aaffff" : "#2C7BE5", flexShrink: 0 }}
                                                                        />
                                                                        {copied && <span style={{ fontSize: '12px', color: "#2C7BE5", flexShrink: 0 }}>Copied!</span>}

                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Remaining fields in standard 2-column layout */}
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Created By</div>
                                                                <div className="field-value">{selectedRow.created_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Created Time</div>
                                                                <div className="field-value">{formatDateTime(selectedRow.created_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Last Modified By</div>
                                                                <div className="field-value">{selectedRow.modified_by || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Last Modified Time</div>
                                                                <div className="field-value">{formatDateTime(selectedRow.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Archived By</div>
                                                                <div className="field-value">{selectedRow.archived_by || "System"}</div>
                                                            </div>
                                                            <div className="field-block" style={{ borderBottom: 'none', display: 'flex', flexDirection: 'column', padding: '0px' }}>
                                                                <div className="field-label">Archived Time</div>
                                                                <div className="field-value">{formatDateTime(selectedRow.archived_at) || formatDateTime(selectedRow.modified_at) || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='bg-white' style={{ border: '1px solid #ffffff', borderRadius: '12px', background: 'var(--Background-White, #FFF)', boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px' }}>

                                                        <div className='px-15 py-20' style={{ borderBottom: '.0625rem solid rgba(46, 54, 80, .125)', color: 'var(--Gray-900, #212529)', padding: '20px 15px', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, lineHeight: 'normal', background: '#F8F9FA', borderRadius: ' 12px 12px 0 0' }}>Business Info</div>
                                                        <div className='bg-white' style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px', border: '1px solid #ffffff', borderRadius: '0 0 12px 12px', boxShadow: '0px 0px 1px rgba(230, 230, 230, 1)', padding: '20px' }}>

                                                            <div className="field-block" style={{
                                                                borderBottom: 'none', display: 'flex', flexDirection: 'column', gridColumn: isCategoryNameOverflowing(selectedRow.parent_name) ? '1 / -1' : '1',
                                                                gridRow: '1', padding: '0px'
                                                            }}>
                                                                <div className="field-label" >Parent Category</div>
                                                                <div className="field-value">
                                                                    <div className='d-flex align-items-center'>
                                                                        {selectedRow?.parent_name !== null ? (
                                                                            <span>{selectedRow.parent_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</span>
                                                                        ) : (
                                                                            <span>{selectedRow.parent_name || <span style={{ color: '#ADB5BD' }}>NA</span>}</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="field-block" style={{
                                                                borderBottom: 'none', display: 'flex', flexDirection: 'column', gridColumn: isCategoryNameOverflowing(selectedRow.parent_name) ? '1' : '2',
                                                                gridRow: isCategoryNameOverflowing(selectedRow.parent_name) ? '2' : '1', padding: '0px'
                                                            }}>
                                                                <div className="field-label">Prefix</div>
                                                                <div className="field-value">{selectedRow.prefix || <span style={{ color: '#ADB5BD' }}>NA</span>}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {drawerTab === 'additional-info' && (
                                                <div className="tab-panel additional-info-panel" style={{ borderRadius: '12px', marginTop: '20px', boxShadow: 'rgba(0, 0, 0, 0.08) 0px 4px 12px' }}>
                                                    <p className="note-type-added cat-notes" style={{ fontSize: '14px', background: 'var(--Gray-100, #F8F9FA)', borderRadius: '12px 12px 0 0', borderBottom: ' 0.0625rem solid rgba(46, 54, 80, 0.125)' }}>Notes</p>
                                                    <label className="no-description-cls notes-preserve" style={{ padding: "20px 20px", background: "var(--Background-White, #FFF)", borderRadius: '0 0 12px 12px' }}>{selectedRow.notes || <span style={{ color: '#ADB5BD' }}>NA</span>}</label>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}